<template>
  <v-container fluid>
    <toolbar mode="application" :item="application">
      <v-spacer />
      <template v-if="item">
        <v-chip close @click:close="uuid = null">{{ item.name }}</v-chip>
      </template>
      <v-btn icon @click="resetPaneSize">
        <v-icon>mdi-arrow-split-vertical</v-icon>
      </v-btn>
      <!-- <v-btn icon @click="drawer = !drawer">
        <v-icon>mdi-dots-vertical</v-icon>
      </v-btn> -->
    </toolbar>
    <v-card flat v-if="item">
      <v-card-text>
        <splitpanes class="default-theme" :class="{ dark: isDark }" @resized="storePaneSize">
          <pane :size="paneSize[0].size" :max-size="paneSize[0].max" :min-size="paneSize[0].min">
            <WellMap 
              :wells="wells" 
              :config="item.config" 
              :target="targets.selected" 
              :group="selectedGroup" 
              :well="selectedWell"
              @selected="onWellSelect"
              @created="onCreateGroup"
            />
            <!-- FILTERS -->
            <v-card flat tile :loading="loading.frame">
              <v-divider />
              <v-toolbar flat tile dense>
                <v-toolbar-title>Filters</v-toolbar-title>
                <v-spacer></v-spacer>
                <ax-button
                  icon="mdi-plus"
                  tooltip="Save as group"
                  @clicked="onCreateGroup"
                ></ax-button>
                <dlg-import-wells @submitted="onImportWells"></dlg-import-wells>
                <dlg-nearby-wells :disabled="!selectedWell" :well="selectedWell" :wells="[]" @submitted="onNearbyWells"></dlg-nearby-wells>
                <!-- <ax-button
                  icon="mdi-content-copy"
                  tooltip="Duplicate current group"
                  :disabled="!selectedGroup"
                  @clicked="onCopyGroup"
                ></ax-button> -->                
                <ax-button icon="mdi-sync" tooltip="Sync groups" @clicked="onSyncGroups" />
                <ax-button icon="mdi-tray-arrow-down" tooltip="Download wells" @clicked="onDownload" :disabled="!selectedGroup"></ax-button>
                <ax-button icon="mdi-close" tooltip="Reset filters" @clicked="onResetFilters" />
                <ax-button :icon="showFilters ? 'mdi-chevron-up' : 'mdi-chevron-down'" tooltip="Toggle filters" @clicked="showFilters = !showFilters" />
              </v-toolbar>
              <v-card-text>
                <ax-groups
                  ref="groupsRef"
                  :uuid="item.uuid"
                  :prediction-id="item.id"
                  :groups="groups"
                  :editable="true"
                  :targets="targets.options.map((t) => t.value)"
                  @group-locked="onGroupLocked"
                ></ax-groups>
              </v-card-text>
              <v-card-text v-if="showFilters">
                <v-row>
                  <v-col cols="3">
                    <v-toolbar flat color="transparent"><v-toolbar-title>Settings</v-toolbar-title></v-toolbar>
                    <v-autocomplete label="Target" outlined dense :items="targets.options" v-model="targets.selected" @change="onChangeSettings" />
                    <v-autocomplete label="Method" outlined dense :items="methods.options" v-model="methods.selected" @change="onChangeSettings" />
                    <v-autocomplete label="Approach" outlined dense :items="approach.options" v-model="approach.selected" @change="onChangeSettings" />
                    <v-autocomplete label="Technique" outlined dense :items="technique.options" v-model="technique.selected" @change="onChangeSettings" />
                    <v-autocomplete label="Timescale" outlined dense :items="timescale.options" v-model="timescale.selected" @change="onChangeSettings" />
                    <v-autocomplete
                      label="Normalize"
                      outlined
                      dense
                      :items="normalize.options"
                      v-model="normalize.selected"
                      :disabled="!hasLateralLength"
                      @change="onChangeSettings"
                    />
                    <v-container>
                      <v-row class="flex-nowrap">
                        <v-checkbox :disabled="!hasLateralLength" v-model="scaling.enabled" class="shrink mt-1" />
                        <v-text-field
                          outlined
                          dense
                          label="Scaling"
                          type="number"
                          hide-spin-buttons
                          v-model.number="scaling.value"
                          :disabled="!scaling.enabled"
                          @change="onChangeSettings"
                          suffix="ft"
                        />
                      </v-row>
                    </v-container>
                  </v-col>
                  <v-col cols="3">
                    <v-toolbar flat color="transparent"><v-toolbar-title>Nominals</v-toolbar-title></v-toolbar>
                    <v-autocomplete v-for="(filter, column, index) in filteredObjects"
                      :key="`${column}_${index}`"
                      v-model="filter.selected"
                      :items="filter.list"
                      multiple
                      outlined
                      dense
                      :label="column"
                      clearable
                      @change="onChangeFilter"
                    >
                      <template v-slot:selection="{ item, index }">
                        <span v-if="index === 0">{{ item }}</span>
                        <span v-if="index === 1" class="ml-1 grey--text text-caption">(+{{ filter.selected.length - 1 }} others)</span>
                      </template>
                    </v-autocomplete>
                    <v-autocomplete label="Quality" outlined dense :items="quality.options" v-model="quality.selected" clearable @change="onChangeSettings" />
                    <!-- <v-autocomplete label="Operator" outlined dense :items="operator.options" v-model="operator.selected" @change="onChangeFilter" /> -->
                  </v-col>
                  <v-col cols="6">
                    <v-toolbar flat color="transparent"><v-toolbar-title>Scalars</v-toolbar-title></v-toolbar>
                    <v-row v-for="(filter, column, index) in filteredNumbers" :key="`${column}_${index}`">
                      <v-col cols="12" md="6">
                        <v-text-field
                          :label="column"
                          type="number"
                          v-model.number="filter.selected[0]"
                          hide-details
                          dense
                          outlined
                          hide-spin-buttons
                          :suffix="getTargetUnits(column)"
                          @change="onChangeFilter"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          :label="column"
                          type="number"
                          hide-details
                          dense
                          outlined
                          hide-spin-buttons
                          v-model.number="filter.selected[1]"
                          :suffix="getTargetUnits(column)"
                          @change="onChangeFilter"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-divider />
              <v-toolbar flat dense>
                <v-toolbar-title>Wells</v-toolbar-title>
                <v-spacer></v-spacer>
                <ax-toggle 
                  :icon="selectionMode === 'single' ? 'mdi-star-outline' : 'mdi-star'"
                  :tooltip="selectionMode === 'single' ? 'Select control wells' : 'Select single well'"
                  :value="selectionMode === 'multi'"
                  @clicked="onToggleSelectionMode"
                ></ax-toggle>
              </v-toolbar>
              <v-divider />
              <v-data-table
                class="text-no-wrap hover"
                v-model="table.selected"
                :items="table.data"
                :headers="table.headers"
                dense show-select
                :single-select="selectionMode === 'single'"
                @click:row="onRowClick"
                :item-key="item.config.api"
              >
                <template v-slot:[`item.Year`]="{ item }">{{ item.Year | yearFormat }}</template>
                <template v-slot:[`item.Length`]="{ item }">{{ item.Length | numberFormat }}</template>
                <template v-slot:[`item.Production`]="{ item }">{{ item.Production | numberFormat((round = true)) }}</template>
                <template v-slot:[`item.EUR`]="{ item }">{{ item.EUR | numberFormat((round = true)) }}</template>
                <template v-slot:[`item.Quality`]="{ item }"><v-icon small :color="item.Quality == 'Predicted' ? 'green' : 'red'">{{ item.Quality == 'Predicted' ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon></template>
                <template v-slot:[`item.Control`]="{ item }">
                  <v-icon v-if="selectedGroup && selectedGroup.control_wells && selectedGroup.control_wells.includes(item[API])" small color="amber">mdi-star</v-icon>
                </template>
              </v-data-table>
            </v-card>
          </pane>
          <pane :size="paneSize[1].size" :max-size="paneSize[1].max" :min-size="paneSize[1].min" style="height: auto">
            <template v-if="!!selectedGroup || !!selectedWell">
              <!-- TOOLBAR -->
              <v-toolbar flat tile dense>
                <v-toolbar-title>
                  <template v-if="!!selectedWell">Analyzing {{ capitalize(targets.selected) }} production of "{{ selectedWell }}"</template>
                  <template v-else>
                    <template v-if="!!selectedGroup">
                      Analyzing {{ capitalize(targets.selected) }} production of {{ selectedGroup.count | numberFormat }} wells</template>
                    </template>
                </v-toolbar-title>
                <v-spacer></v-spacer>
              </v-toolbar>

              <!-- WELL STATS -->
              <v-card flat tile :loading="loading.production">
                <div class="d-flex flex-row">
                  <div class="flex-grow-1">
                    <v-card-title>
                      <span v-if="!selectedWell">Mean&nbsp;</span>
                      Cumulative Production
                    </v-card-title>
                    <v-card-subtitle>
                      <template v-if="!!selectedWell">From {{ months }} months of actual production</template>
                      <template v-else>From up to {{ months }} months of actual production</template>
                    </v-card-subtitle>
                  </div>
                  <div>
                    <v-card-text>
                      <div class="d-flex flex-row align-items-center">
                        <span class="text-h4 mr-1">{{ totalProduction | numberFormat }}</span>
                        <small class="text-muted">{{ productionUnits }}</small>
                      </div>
                    </v-card-text>
                  </div>
                </div>

                <!-- EURs -->
                <div class="d-flex flex-row">
                  <div class="flex-grow-1">
                    <v-card-title>Estimated Ultimate Recovery (EUR)</v-card-title>
                    <v-card-subtitle>{{ eurMessage }}</v-card-subtitle>
                  </div>
                  <div>
                    <v-card-text>
                      <div class="d-flex flex-row align-items-center">
                        <span class="font-weight-medium text-h4 mr-1" :class="colors.text[methods.selected]">
                          {{ getEUR | numberFormat((round = true)) }}
                        </span>
                        <small class="text-muted">{{ targetUnits }}</small>
                      </div>
                    </v-card-text>
                  </div>
                </div>
              </v-card>

              <!-- SUMMARY STATISTICS -->
              <v-card flat tile v-if="!!selectedGroup" :loading="loading.statistics">
                <v-divider />
                <v-toolbar flat tile dense>
                  <v-toolbar-title>Summary Statistics</v-toolbar-title>
                  <v-spacer></v-spacer>
                  <dlg-well-comparison :item="item" :group="selectedGroup" :wells="finalWells()" :payload="requestPayload()" />
                  <dlg-summary-stats :nominals="Object.keys(filteredObjects)" @submitted="onSummaryStats" />
                  <ax-menu
                    :mandatory="false"
                    icon="mdi-format-columns"
                    title="Analysis dimensions"
                    :options="dimensionsList"
                    :default="dimension"
                    model="dimension"
                    @changed="onMenuChange"
                  ></ax-menu>
                </v-toolbar>
                <v-card-text>
                  <v-data-table
                    class="text-no-wrap hover"
                    :headers="statistics.headers"
                    :items="statistics.items"
                    :hide-default-footer="statistics.items.length < 5"
                    :items-per-page="5"
                    dense
                  >
                    <template v-slot:[`header.mean`]="{ header }">{{ header.text }} {{ targetUnits }}</template>
                    <template v-slot:[`header.p1`]="{ header }">{{ header.text }} {{ targetUnits }}</template>
                    <template v-slot:[`header.p10`]="{ header }">{{ header.text }} {{ targetUnits }}</template>
                    <template v-slot:[`header.p50`]="{ header }">{{ header.text }} {{ targetUnits }}</template>
                    <template v-slot:[`header.p90`]="{ header }">{{ header.text }} {{ targetUnits }}</template>
                    <template v-slot:[`header.p99`]="{ header }">{{ header.text }} {{ targetUnits }}</template>
                    <template v-slot:[`header.length`]="{ header }">{{ header.text }} (ft)</template>

                    <template v-slot:[`item.dimension`]="{ item }">{{ String(item.dimension).toUpperCase() }}</template>
                    <template v-slot:[`item.count`]="{ item }">{{ item.count | numberFormat((round = false)) }}</template>
                    <template v-slot:[`item.p1`]="{ item }">{{ item.p1 | numberFormat((round = true)) }}</template>
                    <template v-slot:[`item.p10`]="{ item }">{{ item.p10 | numberFormat((round = true)) }}</template>
                    <template v-slot:[`item.p50`]="{ item }">{{ item.p50 | numberFormat((round = true)) }}</template>
                    <template v-slot:[`item.p55`]="{ item }">{{ item.p55 | numberFormat((round = true)) }}</template>
                    <template v-slot:[`item.p90`]="{ item }">{{ item.p90 | numberFormat((round = true)) }}</template>
                    <template v-slot:[`item.p99`]="{ item }">{{ item.p99 | numberFormat((round = true)) }}</template>
                    <template v-slot:[`item.mean`]="{ item }">{{ item.mean | numberFormat((round = true)) }}</template>
                    <template v-slot:[`item.ratio`]="{ item }">{{ item.ratio.toFixed(4) }}</template>
                    <template v-slot:[`item.length`]="{ item }">{{ item.length | numberFormat }}</template>
                  </v-data-table>
                </v-card-text>
              </v-card>

              <!-- SINGLE WELL -->
              <v-card flat tile min-height="350" v-if="!!selectedWell" :loading="loading.well">
                <v-divider />
                <v-toolbar flat tile dense>
                  <v-toolbar-title>{{ wellName }}</v-toolbar-title>
                  <v-spacer></v-spacer>
                  <ax-menu
                    icon="mdi-axis-arrow"
                    title="Chart axis"
                    :options="chart_scales.options"
                    :default="chart_scales.selected"
                    model="chart_scales.selected"
                    @changed="onMenuChange"
                  ></ax-menu>

                  <ax-toggle icon="mdi-magnify" tooltip="Toggle plot" :value="showTermination" @clicked="showTermination = $event"></ax-toggle>
                  <ax-button 
                    v-show="!!selectedGroup" 
                    :icon="isControlWell ? 'mdi-star' : 'mdi-star-outline'" 
                    :tooltip="isControlWell ? 'Unset as control well' : 'Set as control well'" 
                    @clicked="onToggleControlWell"
                  ></ax-button>
                  <dlg-edit-well
                    v-if="!!well"
                    :key="selectedWell"
                    :uuid="uuid"
                    :api="selectedWell"
                    :targets="clone(this.targets.options)"
                    :methods="clone(this.methods.options)"
                    :techniques="clone(this.technique.options)"
                    :termination:="item.config.termination"
                    :settings="clone(requestSettings())"
                    @submitted="onEditWell"
                  ></dlg-edit-well>
                  <ax-button v-show="!!selectedGroup && canRemoveWell" icon="mdi-minus" tooltip="Remove well from group" @clicked="onRemoveWell"></ax-button>
                  <ax-button icon="mdi-export" tooltip="Export well" @clicked="onExportSingleWell"></ax-button>
                  <ax-button icon="mdi-close" tooltip="Unselect well" @clicked="selectedWell = null"></ax-button>
                </v-toolbar>
                <v-card-text>
                  <vue-plotly id="single" class="chart-container" :data="charts.single.data" :layout="charts.single.layout" :options="charts.single.options" />
                </v-card-text>
                <v-data-table class="text-no-wrap hover" :items="getWellDetails" :headers="table.headers" dense hide-default-footer>
                  <template v-slot:[`item.Year`]="{ item }">{{ item.Year | yearFormat }}</template>
                  <template v-slot:[`item.Length`]="{ item }">{{ item.Length | numberFormat }}</template>
                  <template v-slot:[`item.oil`]="{ item }">{{ item.oil | numberFormat((round = true)) }}</template>
                  <template v-slot:[`item.gas`]="{ item }">{{ item.gas | numberFormat((round = true)) }}</template>
                  <template v-slot:[`item.water`]="{ item }">{{ item.water | numberFormat((round = true)) }}</template>
                  <template v-slot:[`item.EUR`]="{ item }">{{ item.EUR | numberFormat((round = true)) }}</template>
                </v-data-table>
              </v-card>

              <!-- AGGREGATES -->
              <v-card flat tile v-if="!selectedWell" :loading="loading.typeCurves || loading.terminalDeclines">
                <v-divider />
                <v-toolbar flat tile dense>
                  <v-toolbar-title>Aggregates</v-toolbar-title>
                  <v-spacer></v-spacer>
                  <!-- <ax-menu
                    icon="mdi-sigma"
                    title="Aggregates"
                    :options="aggregates.options"
                    :default="aggregates.selected"
                    model="aggregates.selected"
                    @changed="onMenuChange"
                  ></ax-menu> -->
                  <ax-menu
                    icon="mdi-axis-arrow"
                    title="Chart axis"
                    :options="chart_scales.options"
                    :default="chart_scales.selected"
                    model="chart_scales.selected"
                    @changed="onMenuChange"
                  ></ax-menu>
                  <ax-menu
                    icon="mdi-shuffle"
                    title="Calculation method"
                    :options="calculations.options"
                    :default="calculations.selected"
                    model="calculations.selected"
                    @changed="onMenuChange"
                  ></ax-menu>
                  <ax-toggle icon="mdi-magnify" tooltip="Toggle plot" :value="showTermination" @clicked="showTermination = $event"></ax-toggle>
                  <v-divider vertical inset class="mx-2" />
                  <dlg-decline-type-curves
                    :months="termination.duration.value"
                    @submitted="onDeclineTypeCurves"
                    :disabled="selectedGroup.locked"
                  ></dlg-decline-type-curves>
                  <dlg-curve-termination
                    v-if="hasParams"
                    :termination="termination"
                    :target_units="targets.options.find((t) => t.value == targets.selected).units"
                    :disabled="selectedGroup.locked"
                    @submitted="onCurveTermination"
                  ></dlg-curve-termination>
                  <dlg-well-termination
                    v-if="!hasParams"
                    :aggregates="hasParams"
                    :termination="termination"
                    :target_units="targets.options.find((t) => t.value == targets.selected).units"
                    :approach="approach.selected"
                    :disabled="selectedGroup.locked"
                    @submitted="onWellTermination"
                  ></dlg-well-termination>
                  <dlg-edit-type-curves
                    :options="editableCurveOptions"
                    :params="groupParams"
                    @submitted="onEditTypeCurves"
                    :disabled="selectedGroup.locked || !hasParams"
                  />
                  <ax-button
                    icon="mdi-trash-can-outline"
                    tooltip="Clear typecurves"
                    :disabled="selectedGroup.locked || !(hasParams || hasCurves || hasTermination)"
                    @clicked="onClearDeclines"
                  ></ax-button>
                  <v-divider vertical inset class="mx-2" />
                  <dlg-import-type-curves @submitted="onImportTypeCurves"></dlg-import-type-curves>
                  <dlg-export-type-curves
                    @submitted="onExportTypeCurves"
                    :targets="clone(targets.options)"
                    :declined-targets="declinedTargets"
                    :mean-targets="meanTargets"
                    :methods="clone(methods.options)"
                    :techniques="clone(this.technique.options)"
                    :group-method="!!selectedGroup && !!selectedGroup.settings ? selectedGroup.settings.method : null"
                    :group-technique="!!selectedGroup && !!selectedGroup.settings ? selectedGroup.settings.technique : null"
                    :declined="hasParams"
                    :terminated="this.termination.method == 'decline'"
                  />
                </v-toolbar>
                <v-card-text>
                  <vue-plotly
                    id="aggregates"
                    class="chart-container"
                    v-if="!isEmpty(charts.aggregates.data)"
                    :data="charts.aggregates.data"
                    :layout="charts.aggregates.layout"
                    :options="charts.aggregates.options"
                  />
                </v-card-text>
              </v-card>

              <!-- PROBIT -->
              <v-card flat tile v-if="!!selectedGroup" :loading="loading.probit">
                <v-divider />
                <v-toolbar flat tile dense>
                  <v-toolbar-title>Probit Plot</v-toolbar-title>
                  <v-spacer />
                  <dlgPercentiles :min="10" :max="90" :step="5" @submitted="onPercentiles" />
                  <ax-button v-if="canRemoveWell" icon="mdi-minus" tooltip="Remove well from group" @clicked="onRemoveWell"></ax-button>
                  <ax-button :icon="showProbit ? 'mdi-chevron-up' : 'mdi-chevron-down'" tooltip="Toggle probit" @clicked="showProbit = !showProbit" />
                </v-toolbar>
                <v-card-text v-if="showProbit">
                  <vue-plotly
                    id="probit"
                    class="chart-container"
                    v-if="!isEmpty(charts.probit.data)"
                    :data="charts.probit.data"
                    :layout="charts.probit.layout"
                    :options="charts.probit.options"
                    @selected="onProbitSelect"
                    @click="onProbitClick"
                  />
                </v-card-text>
              </v-card>

              <!-- RATIOS -->
              <v-card flat tile v-if="canComputeRatios" :loading="loading.ratios">
                <v-divider />
                <v-toolbar flat tile dense>
                  <v-toolbar-title>Ratios ({{String(ratios.selected).toUpperCase()}})</v-toolbar-title>
                  <v-spacer></v-spacer>
                  <ax-menu
                    v-if="canComputeRatios"
                    :mandatory="true"
                    icon="mdi-division"
                    title="Ratios"
                    :options="ratios.options.filter((r) => r.visible)"
                    :default="ratios.selected"
                    model="ratios.selected"
                    @changed="onMenuChange"
                  ></ax-menu>
                  <ax-toggle icon="mdi-magnify" tooltip="Toggle plot" :value="showTermination" @clicked="showTermination = $event"></ax-toggle>
                  <ax-button :icon="showRatios ? 'mdi-chevron-up' : 'mdi-chevron-down'" tooltip="Toggle ratios" @clicked="showRatios = !showRatios" />
                </v-toolbar>
                <v-card-text v-if="showRatios">
                  <vue-plotly
                    id="ratios"
                    class="chart-container"
                    :data="charts.ratio.data"
                    :layout="charts.ratio.layout"
                    :options="charts.ratio.options"
                  />
                </v-card-text>
              </v-card>
            </template>
            <v-card-text v-else
                         class="pa-16 grey--text text-center"
                         :class="[isDark ? 'text--lighten' : 'text--darken']">
              <img src="@/assets/images/ax_logomark.svg"
                   width="256"
                   height="256"
                   contain
                   class="greyscale mt-16 mb-4" />
              <p>Select a well or a group of wells</p>
            </v-card-text>
          </pane>
        </splitpanes>
      </v-card-text>
    </v-card>
    <dlg-open-file v-else mode="prediction" @submitted="uuid=$event" />
  </v-container>
</template>
<script>
import Predictions from "@/api/predictions"
import Techniques from "@/api/techniques"
import Forecasting from "@/api/forecasting"
import XLSX from "xlsx"
import DataFrame from "dataframe-js"
import * as F from "@/modules/forecast"
import { deriveMethodTechniqueAbbr } from "@/utils/aries"
import toolbar from "@/components/toolbar"
import VuePlotly from "@statnett/vue-plotly"
import AxButton from "@/components/button"
import AxToggle from "@/components/toggle"
import AxMenu from "@/components/menu"
import dlgOpenFile from "@/components/open"
import AxGroups from "@/components/Apps/Forecast/Groups"
import WellMap from "@/components/Apps/Forecast/WellMap"
import dlgImportTypeCurves from "@/components/Apps/Forecast/Dialogs/ImportTypeCurves"
import dlgRunEconomics from "@/components/Apps/Forecast/Dialogs/RunEconomics"
import dlgDeclineTypeCurves from "@/components/Apps/Forecast/Dialogs/DeclineTypeCurves"
import dlgExportTypeCurves from "@/components/Apps/Forecast/Dialogs/ExportTypeCurves"
import dlgEditTypeCurves from "@/components/Apps/Forecast/Dialogs/EditTypeCurves"
import dlgNearbyWells from "@/components/Apps/Forecast/Dialogs/NearbyWells"
import dlgImportWells from "@/components/Apps/Forecast/Dialogs/ImportWells"
import dlgWellTermination from "@/components/Apps/Forecast/Dialogs/WellTermination"
import dlgCurveTermination from "@/components/Apps/Forecast/Dialogs/CurveTermination"
import dlgSummaryStats from "@/components/Apps/Forecast/Dialogs/SummaryStats"
import dlgFixWells from "@/components/Apps/Forecast/Dialogs/FixWells"
import dlgEditWell from "@/components/Apps/Forecast/Dialogs/EditWell"
import dlgPercentiles from "@/components/Apps/Forecast/Dialogs/Percentiles"
import dlgWellComparison from "@/components/Apps/Forecast/Dialogs/WellComparison"
import { Splitpanes, Pane } from "splitpanes"
import "splitpanes/dist/splitpanes.css"

// Every target a prediction can have; each prediction shows the subset it was run with
const TARGET_OPTIONS = Object.freeze([
  { text: "Oil", value: "oil", units: "bbl", color: "#4CAF50", code: 'green' },
  { text: "Gas", value: "gas", units: "mcf", color: "#F44336", code: 'red' },
  { text: "Water", value: "water", units: "bbl", color: "#03A9F4", code: 'light-blue' },
])

export default {
  props: ["application"],
  components: {
    toolbar,
    AxGroups,
    VuePlotly,
    AxButton,
    AxToggle,
    AxMenu,
    WellMap,
    dlgOpenFile,
    dlgImportTypeCurves,
    dlgDeclineTypeCurves,
    dlgExportTypeCurves,
    dlgEditTypeCurves,
    dlgNearbyWells,
    dlgSummaryStats,
    dlgRunEconomics,
    dlgImportWells,
    dlgWellTermination,
    dlgCurveTermination,
    dlgFixWells,
    dlgPercentiles,
    dlgEditWell,
    dlgWellComparison,
    Splitpanes,
    Pane,
  },
  data() {
    return {
      uuid: null,
      item: null,
      drawer: false,
      loading: {
        map: false,
        frame: false,
        production: false,
        statistics: false,
        typeCurves: false,
        probit: false,
        ratios: false,
        well: false,
        terminalDeclines: false,
      },
      approach: {
        selected: "forecast",
        options: [
          { text: "Forecast and Aggregate", value: "forecast" },
          { text: "Aggregate and Forecast", value: "aggregate" },
        ],
      },
      technique: {
        selected: null,
        options: [],
      },
      showFilters: true,
      showProbit: true,
      showRatios: true,
      selectionMode: 'single', // 'single' or 'multi'
      table: {
        search: null,
        selected: [],
        data: [],
        headers: [],
      },
      statistics: {
        headers: [
          // { text: "Dimension", value: "dimension" },
          { text: "N", value: "count" },
          { text: "Mean", value: "mean" },
          { text: "P1", value: "p1", quantile: 0.01 },
          { text: "P10", value: "p10", quantile: 0.1 },
          { text: "P50", value: "p50", quantile: 0.5 },
          { text: "P90", value: "p90", quantile: 0.9 },
          { text: "P99", value: "p99", quantile: 0.99 },
          { text: "P90/P10", value: "ratio" },
        ],
        items: [],
      },
      quality: {
        selected: undefined,
        options: ["Predicted", "Unresolved"],
        data: {}
      },
      revenue: {},
      production: {},
      type_curves: {
        options: [
          { text: "P90", value: "p90", type: "p", quantile: 0.9 },
          { text: "P50", value: "p50", type: "p", quantile: 0.5 },
          { text: "P10", value: "p10", type: "p", quantile: 0.1 },
        ],
        imported: undefined,
        aggregates: {},
      },
      timescale: {
        selected: "relative",
        options: [
          { text: "Relative", value: "relative" },
          { text: "Actual", value: "absolute" },
        ],
      },
      calculations: {
        selected: "actual",
        options: [
          { text: "Actual", value: "actual" },
          { text: "Cumulative", value: "cumulative" },
        ],
      },
      // aggregates: {
      //   selected: "mean",
      //   options: [
      //     { text: "Mean", value: "mean" },
      //     { text: "Sum", value: "sum" },
      //   ],
      // },
      history: {
        enabled: true,
        min: 1,
        max: 11,
        value: 6,
      },
      predictions: {},
      well: null,
      selectedWell: null,
      results: [],
      wells: [],
      parameters: [],
      groups: {
        selected: undefined,
        list: [],
      },
      dimension: undefined,
      filters: {},
      showTermination: false,
      normalize: {
        selected: "not",
        options: [
          { text: "Not normalized", value: "not" },
          { text: "Per foot of Lateral Length", value: "foot" },
          { text: "Per 1,000 feet of Lateral Length", value: "feet" },
        ],
      },
      scaling: {
        enabled: false,
        value: null,
      },
      lines: {
        // selected: ["p10", "p50", "p90", "avg", "w10", "w50", "w90"],
        // selected: ["p10", "p50", "p90"],
        options: [
          { text: "P90", value: "p90", type: "p", quantile: 0.9 },
          { text: "P50", value: "p50", type: "p", quantile: 0.5 },
          { text: "P10", value: "p10", type: "p", quantile: 0.1 },
          // { text: "W90", value: "w90", type: 'w', quantile: 90 },
          // { text: "W50", value: "w50", type: 'w', quantile: 50 },
          // { text: "W10" value: "w10", type: 'w', quantile: 10 },
          // { text: "Average", value: "avg", type: 'a', quantile: 50 },
        ],
      },
      methods: {
        selected: null,
        options: [],
      },
      settings: {
        oil: {
          min: 0,
          max: 170,
          value: 35,
        },
        gas: {
          min: 0,
          max: 5,
          value: 3,
        },
        ngl: {
          min: 0,
          max: 20,
          value: 3,
        },
        shrinkage: {
          min: 0,
          max: 100,
          value: 5,
        },
        ngl_factor: {
          min: 0,
          max: 100,
          value: 5,
        },
        statistics: {
          showSwanson: true,
          p10: 0.3,
          p50: 0.4,
          p90: 0.3,
        },
      },
      termination: {
        method: "decline",
        options: [
          { text: "Duration", value: "duration", disabled: false },
          { text: "Monthly Production", value: "production", disabled: false },
          { text: "Decline Rate", value: "decline", disabled: false },
        ],
        duration: {
          min: 0,
          max: 600,
          value: 600,
          step: 12,
          units: "months",
        },
        production: {
          min: 0,
          max: 10000,
          value: 0,
          step: 10,
          units: "bbl",
        },
        decline: {
          min: 0.0,
          max: 15.0,
          value: 5.0,
          step: 0.1,
          units: "%",
        },
        // exponential: false,
        // individual: true,
      },
      colors: {
        curves: {
          p10: "#F44336",
          p50: "#4CAF50",
          p90: "#2196F3",
          avg: "#B0BEC5",
          sum: "#78909C",
          total: "#B0BEC5",
          w10: "#F44336",
          w50: "#4CAF50",
          w90: "#2196F3",
        },
        lines: {
          history: "#BDBDBD",
          arps1: "#F44336",
          advanced3a: "#2196F3",
          advanced5: "#2196F3",
          multi3a: "#FF9800",
          multi5: "#FF9800",
          hyper5: "#9C27B0",
          hyper5a: "#1B5E20",
        },
        text: {
          arps1: "error--text",
          advanced3a: "primary--text",
          advanced5: "primary--text",
          multi3a: "orange--text",
          multi5: "orange--text",
          hyper5: "purple--text",
          hyper5a: "green--text",
        },
      },
      targets: {
        selected: "oil",
        options: TARGET_OPTIONS.map((t) => ({ ...t })),
      },
      probit: {},
      ratios: {
        data: null,
        selected: "gor",
        options: [
          {text: 'Gas/Oil', value: 'gor'}, 
          {text: 'Water/Oil', value: 'wor'}, 
          {text: 'Water/Gas', value: 'wgr'}
        ]
      },
      chart_scales: {
        selected: "log",
        options: [
          { text: "Linear", value: "linear" },
          { text: "Logarithmic", value: "log" },
        ],
      },
      charts: {
        aggregates: {
          data: [],
          layout: {
            autosize: true,
            barmode: "relative",
            showlegend: true,
            height: 300,
            // legend: { x: 1, y: 1, xanchor: "right" },
            font: { family: "Roboto, sans-serif", color: "#808080" },
            hovermode: "closest",
            margin: { l: 45, t: 15, r: 15, b: 45 },
            xaxis: { title: { text: `Time (months)` }, dtick: 12 },
            yaxis: { title: { text: `Production` }, type: "log" },
            // yaxis2: {
            //   overlaying: "y",
            //   side: "right",
            //   type: "log",
            //   showgrid: false,
            //   zeroline: false,
            //   showline: false,
            //   autotick: true,
            //   ticks: "",
            //   showticklabels: false,
            // },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
          },
          options: {
            //displayModeBar: true,
            displaylogo: false,
            responsive: true,
          },
        },
        probit: {
          data: [],
          layout: {
            autosize: true,
            showlegend: true,
            height: 300,
            // legend: { x: 1, y: 0.5, xanchor: "right" },
            font: { family: "Roboto, sans-serif", color: "#808080" },
            hovermode: "closest",
            margin: { l: 45, t: 15, r: 15, b: 45 },
            xaxis: { title: { text: `Production` }, type: "log" },
            yaxis: {
              title: { text: `Percentile Rank` },
              type: "linear",
              tickmode: "array",
              flipped: false,
              tickvals: [0.01, 0.02, 0.05, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 0.95, 0.98, 0.99],
              ticktext: ["", "", "", "P10", "", "", "", "P50", "", "", "", "P90", "", "", ""],
            },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
          },
          options: {
            // displayModeBar: true,
            displaylogo: false,
            modeBarButtonsToRemove: [
              "zoom2d",
              "lasso2d",
              "autoScale2d",
              "toggleSpikelines",
              //"toImage",
              "toggleHover",
              "hoverClosestGl2d",
              "hoverClosestPie",
            ],
            responsive: true,
          },
        },
        single: {
          data: [],
          layout: {
            autosize: true,
            height: 300,
            showlegend: true,
            // legend: { x: 1, y: 1, xanchor: "right" },
            font: { family: "Roboto, sans-serif", color: "#808080" },
            xaxis: { title: { text: "Time (months)" }, dtick: 12 },
            yaxis: { title: { text: "Production" }, type: "log" },
            hovermode: "closest",
            margin: { l: 45, t: 15, r: 15, b: 45 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
          },
          options: {
            // displayModeBar: true,
            displaylogo: false,
            responsive: true,
          },
        },
        ratio: {
          data: [],
          layout: {
            autosize: true,
            height: 300,
            showlegend: true,
            // legend: { x: 1, y: 1, xanchor: "right", bgcolor: "#FFF" },
            font: { family: "Roboto, sans-serif", color: "#808080" },
            xaxis: { title: { text: "Time (months)" }, dtick: 12 },
            yaxis: { title: { text: "Production" }, type: "log" },
            yaxis2: {
              overlaying: "y",
              side: "right",
              type: "log",
              showgrid: false,
              zeroline: false,
              showline: false,
              autotick: true,
              ticks: "",
              showticklabels: true,
            },
            hovermode: "closest",
            margin: { l: 45, t: 15, r: 15, b: 45 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
          },
          options: {
            // displayModeBar: true,
            displaylogo: false,
            responsive: true,
          },
        },
      },
      unwatch: null,
    }
  },
  computed: {
    API() {
      // let key = 'name' in this.item.config ? this.item.config.name : this.item.config.api
      return this.item.config.api
    },
    LATLEN() {
      let name = "Lat Len - Horz Disp"
      if (name in this.item.config.features && !!this.item.config.features[name]) {
        return this.item.config.features[name]
      }
      return undefined
    },
    hasName() {
      return 'name' in this.item.config && this.item.config.name
    },
    filteredObjects() {
      if (!this.isEmpty(this.filters)) {
        return Object.fromEntries(Object.entries(this.filters).filter(([k, v]) => ["object", "datetime64[ns]"].includes(v.type)))
      }
      return {}
    },
    filteredNumbers() {
      if (!this.isEmpty(this.filters)) {
        return Object.fromEntries(Object.entries(this.filters).filter(([k, v]) => ["int8", "float8", "int16", "float16", "int32", "float32", "int64", "float64"].includes(v.type)))
      }
      return {}
    },
    filteredScalars() {
      if (!this.isEmpty(this.filters)) {
        return Object.keys(Object.fromEntries(Object.entries(this.filters).filter(([k, v]) => ["int8", "float8", "int16", "float16", "int32", "float32", "int64", "float64"].includes(v.type) && !Object.keys(this.item.config.targets).includes(k.toLowerCase()))))
      }
      return []
    },
    selectedGroup() {
      if (this.isEmpty(this.groups.selected)) return undefined
      return this.groups.list.find(g => g.id == this.groups.selected)
    },
    groupParams() {
      if (!this.hasParams) return {}
      return this.selectedGroup.targets[this.targets.selected].params
    },
    groupCurves() {
      if (!this.hasCurves) return {}
      return this.selectedGroup.targets[this.targets.selected].curves
    },
    declinedCurves() {
      if (!this.hasParams) return {}
      let curves = {}
      const months = [...Array(this.termination.duration.max).keys()].map((x) => x + 1)
      Object.entries(this.selectedGroup.targets[this.targets.selected]['params']).forEach(([key, params]) => {
        curves[key] = months.map((month) => {
          return F.arps(params.Q, params.D, params.B, month)
        })
      })
      return curves
    },
    hasLateralLength() {
      if (!!this.item) {
        if ("filters" in this.item.config) {
          return !!this.LATLEN && this.item.config.filters.includes(this.LATLEN)
        }
      }
      return false
    },
    hasRatioTarget() {
      let targets = this.targets.options.map((t) => t.value)
      if (targets.length < 2) return false
      let matches = []
      if (this.ratios.selected == "gor") {
        matches = ["gas", "oil"]
      } else if (this.ratios.selected == "wor") {
        matches = ["water", "oil"]
      } else if (this.ratios.selected == "wgr") {
        matches = ["water", "gas"]
      }
      return targets.filter((t) => matches.includes(t)).length == 2
    },
    hasOilGas() {
      let targets = this.targets.options.map((t) => t.value)
      return !!this.selectedGroup && (targets.includes("oil") || targets.includes("gas"))
    },
    canComputeRatios() {
      if (!this.isEmpty(this.charts.ratio.data)) return true
      if (this.hasRatioTarget) return true
      return false
    },
    getEUR() {
      if (!!this.selectedWell) {
        if (!!this.well) {
          let production = this.well.production
          let prediction = this.well.predictions[this.methods.selected]
          let decline = this.well.declines[this.methods.selected]
          return this.sum(production) + this.sum(prediction) + this.sum(decline.slice(production.length + prediction.length))
        }
      }
      if (!!this.selectedGroup) {
        let data = this.charts.aggregates.data.find(d => d.name == 'P50' && d.showlegend == false)
        if (!!data) return this.sum(data.y)
        data = this.charts.aggregates.data.find(d => d.name == 'P50' && d.showlegend == true)
        if (!!data) return this.sum(data.y)
      }
      return 0
    },
    months() {
      if (!!this.selectedWell) {
        if (this.well) return this.well.production.length
      }
      if (!this.isEmpty(this.wells)) {
        return Math.max(...this.wells.map(x => x['Months']))
      }
      return 0
    },
    wellParams() {
      return this.parameters.find((well) => well[this.API] == this.selectedWell)
    },
    show_map() {
      return this.item.config.latitude != null && this.item.config.longitude != null
    },
    // show_gor() {
    //   if (this.isObject(this.item.config)) {
    //     if ("oil" in this.item.config.targets && "gas" in this.item.config.targets)
    //       if (this.item.config.targets["oil"] != null && this.item.config.targets["gas"] != null) return true
    //     return false
    //   }
    //   return false
    // },
    totalProduction() {
      let value = 0
      if (!!this.selectedWell && this.well) {
        value = this.sum(this.well.production)
      } else if (!this.isEmpty(this.production)) {
        value = this.production["mean"]
      }
      return value
    },
    productionUnits() {
      let units = undefined
      units = this.targets.options.find((t) => t.value == this.targets.selected).units
      return this.normalize.selected == "not" ? units : this.normalize.selected == "foot" ? `${units}/ft` : `${units}/kft`
    },
    targetUnits() {
      let units = undefined
      units = this.targets.options.find((t) => t.value == this.targets.selected).units
      return this.normalize.selected == "not" ? `EUR (${units})` : this.normalize.selected == "foot" ? `EUR/ft (${units})` : `EUR/kft (${units})`
    },
    getWellDetails() {
      return this.table.data.filter((w) => w[this.API] == this.selectedWell)
    },
    dimensionsList() {
      let dim = this.filterObject(this.filters, "type", "object")
      if ("name" in this.item.config) {
        dim = dim.filter((d) => d.value !== this.item.config.name)
      }
      dim.unshift({ text: "Methods", value: "methods" })
      dim.push({ text: "Start Year", value: "Start Date" })
      return dim
    },
    wellName() {
      if (!!this.selectedWell) {
        if ("name" in this.item.config && this.item.config.name !== null) {
          const well = this.wells.find(x => x[this.API] == this.selectedWell)
          if (well) {
            return well[this.item.config.name]
          }
        }
        return this.selectedWell
      }
      return "Well Name"
    },
    eurMessage() {
      if (this.isDeclined) {
        if (this.termination.method == "duration") {
          return `Declined forecast for up to ${ this.termination.duration.value } months (${ Math.round(this.termination.duration.value / 12) } years) of production`
        } else if (this.termination.method == "production") {
          return `Declined forecast for up to ${ this.termination.production.value } bbl of production per month`
        } else if (this.termination.method == "decline") {
          return `Declined forecast for up to ${ this.termination.decline.value }% per month`
        }
      } else {
        if (this.approach.selected == 'forecast') {
          return `Forecast for up to ${ this.item.config.months } months (${ Math.round(this.item.config.months / 12) } years) of production`
        }
        return `Forecast for up to ${ this.months + 12 } months (${ Math.round((this.months + 12) / 12) } years) of production`
      }
    },
    isDeclined() {
      return !this.isEmpty(this.type_curves.parameters)
    },
    hasParams() {
      if (!this.selectedGroup) return false
      if (!(this.targets.selected in this.selectedGroup.targets)) return false
      if (!('params' in this.selectedGroup.targets[this.targets.selected])) return false
      if (this.isEmpty(this.selectedGroup.targets[this.targets.selected].params)) return false
      return true
    },
    hasCurves() {
      if (!this.selectedGroup) return false
      if (!(this.targets.selected in this.selectedGroup.targets)) return false
      if (!('curves' in this.selectedGroup.targets[this.targets.selected])) return false
      if (this.isEmpty(this.selectedGroup.targets[this.targets.selected].curves)) return false
      return true
    },
    hasTermination() {
      if (!this.selectedGroup) return false
      if (!(this.targets.selected in this.selectedGroup.targets)) return false
      if (!('termination' in this.selectedGroup.targets[this.targets.selected])) return false
      if (this.isEmpty(this.selectedGroup.targets[this.targets.selected].termination)) return false
      return true
    },
    // P-curves plus Mean, when this group's fit for the active target has
    // one -- type_curves.options itself stays P-curves only since it also
    // drives the fit input, and Mean isn't always available (see
    // onDeclineTypeCurves).
    editableCurveOptions() {
      if (this.hasParams && this.groupParams.Mean) {
        return [{ text: "Mean", value: "mean" }, ...this.type_curves.options]
      }
      return this.type_curves.options
    },
    // Targets whose declined type curve includes a Mean fit.
    meanTargets() {
      if (!this.selectedGroup) return []
      return Object.entries(this.selectedGroup.targets)
        .filter(([, t]) => !!t.params && !!t.params.Mean)
        .map(([target]) => target)
    },
    declinedTargets() {
      if (!this.selectedGroup) return []
      return Object.entries(this.selectedGroup.targets)
        .filter(([, t]) => !this.isEmpty(t.params))
        .map(([target]) => target)
    },
    canRemoveWell() {
      if (!this.selectedWell) return false
      if (!this.selectedGroup) return false
      if (!!this.selectedGroup.locked) return false
      if (this.selectedGroup.wells.includes(this.selectedWell)) return true
      return true
    },
    isControlWell() {
      if (!this.selectedWell) return false
      if (!this.selectedGroup) return false
      if (!this.selectedGroup.control_wells) return false
      return this.selectedGroup.control_wells.includes(this.selectedWell)
    },
  },
  watch: {
    uuid: function (val, old) {
      if (val) {
        this.$router.replace({ hash: val })
        Promise.all([
          Predictions.retrieve(this.uuid),
          Techniques.list(),
        ]).then(([response, techniques]) => {
          this.item = response.data
          this.technique.options = techniques.data.map((t) => { return { text: t.name, value: t.slug } })
          this.technique.options = this.technique.options.filter((o) => this.item.config['techniques'].includes(o.value))
          this.technique.selected = this.technique.options[0].value
        })
      } else {
        this.item = null
      }
    },
    item: function (val, old) {
      if (val) {
        // Populate Methods
        this.methods.options = this.item.methods.map((e) => {
          return { text: e.name, value: e.slug }
        })
        this.methods.options.push(this.methods.options.splice(0, 1)[0])
        this.methods.selected = this.methods.options[0].value

        // Convert ITEM settings to JSON
        if (!this.isObject(this.item.config)) this.item.config = this.json_parse(this.item.config)

        // Remove targets that are null
        this.item.config.targets = Object.fromEntries(Object.entries(this.item.config.targets).filter(([_, v]) => v != null))
        // Filter from the full list, not the previous prediction's subset, or targets are lost when switching
        this.targets.options = TARGET_OPTIONS.filter((f) => Object.keys(this.item.config.targets).includes(f.value)).map((t) => ({ ...t }))
        // Set selected target
        this.targets.selected = this.targets.options.map((t) => t.value)[0]

        // Hide unwanted ratios
        let T = this.targets.options.map(t => t.value)
        this.ratios.options.forEach((o) => {
          if (o.value == "gor") {
            this.$set(o, 'visible', ["oil", "gas"].every((i) => T.includes(i)))
          } else if (o.value == "wor") {
            this.$set(o, 'visible', ["oil", "water"].every((i) => T.includes(i)))
          } else if (o.value == "wgr") {
            this.$set(o, 'visible', ["gas", "water"].every((i) => T.includes(i)))
          }
        })
        // Don't carry over a ratio this prediction can't compute (e.g. GOR without gas)
        if (!this.ratios.options.some((o) => o.visible && o.value == this.ratios.selected)) {
          const available = this.ratios.options.find((o) => o.visible)
          if (available) this.ratios.selected = available.value
        }

        if ("months" in this.item.config && this.item.config.months) {
          this.termination.duration.max = this.item.config.months
          this.termination.duration.value = this.item.config.months
        }

        this.fetchFilters()
        this.fetchMapData()
        this.groups.list = this.item.groups == null ? [] : this.item.groups
        this.toggleDarkMode(this.isDark)
      } else {
        this.table.data = []
        this.wells = []
        this.filters = {}
        this.type_curves.aggregates = {}
        this.statistics.items = []
        this.statistics.headers = this.statistics.headers.filter((h) => h.value !== "dimension")
        this.production = {}
        this.groups.list = []
        this.groups.selected = undefined
        this.dimension = undefined
        this.targets.selected = this.targets.options[0].value
        this.methods.selected = this.methods.options[0].value
        this.approach.selected = this.approach.options[0].value
        this.technique.selected = this.technique.options[0].value
        this.quality.selected = undefined
      }
    },
    "scaling.enabled": function (val) {
      if (!val) {
        this.scaling.value = null
        this.onChangeFilter(true)
      }
    },
    selectedWell(val) {
      if (val) {
        // Add to map and probit plots
        this.selectPointsOn("probit", [val])
        this.fetchWellData()
        this.fetchRatios()
      } else {
        // Remove from all plots
        this.charts.single.data = []
        this.fetchRatios()
        this.selectPointsOn("probit")
      }
    },
    "chart_scales.selected": function () {
      this.charts.single.layout.yaxis.type = this.chart_scales.selected
      this.charts.aggregates.layout.yaxis.type = this.chart_scales.selected
      this.charts.ratio.layout.yaxis2.type = this.chart_scales.selected
    },
    "calculations.selected": function () {
      if (!!this.selectedWell) this.plotSingleWell()
      else this.plotTypeCurves()
    },
    showTermination: function () {
      this.showTermination = this.showTermination || false
      if (!!this.selectedWell) {
        this.plotSingleWell()
      } else {
        this.plotTypeCurves()
      }
      this.plotRatios()
    },
    "history.enabled": function () {
      this.history.enabled = this.history.enabled ? this.history.enabled : false
    },
    "groups.selected": function (val, old) {
      this.selectedWell = null
      this.selectionMode = 'single' // Reset to single-select mode when changing groups
      if (!!val) {
        this.setFilters()
        this.fetchAggregates()
      } else {
        this.table.data = []
        this.clearFilters()
        this.type_curves.curves = {}
        this.type_curves.parameters = {}
        this.type_curves.aggregates = {}
        this.table.selected = []
      }
    },
    "table.selected": function (val, old) {
      if (this.selectionMode === 'single') {
        // Single-select mode: select well for single well view
        if (val.length > 0) {
          this.selectedWell = val[0][this.API]
        } else {
          this.selectedWell = null
        }
      } else {
        // Multi-select mode: manage control wells
        if (!this.selectedGroup) return
        
        // Determine which wells were added or removed
        const oldWells = (old || []).map(item => String(item[this.API]))
        const newWells = (val || []).map(item => String(item[this.API]))
        
        // Find newly selected wells (not in old but in new)
        const added = newWells.filter(w => !oldWells.includes(w))
        // Find deselected wells (in old but not in new)
        const removed = oldWells.filter(w => !newWells.includes(w))
        
        // Update control_wells array
        let controlWells = this.selectedGroup.control_wells || []
        
        // Add newly selected wells to control_wells
        added.forEach(well => {
          if (!controlWells.includes(well)) {
            controlWells.push(well)
          }
        })
        
        // Remove deselected wells from control_wells
        removed.forEach(well => {
          const index = controlWells.indexOf(well)
          if (index > -1) {
            controlWells.splice(index, 1)
          }
        })
        
        // Update the group
        this.$set(this.selectedGroup, 'control_wells', controlWells)
        
        // Save groups after updating control wells
        this.saveGroups()
      }
    },
    "selectedGroup.control_wells": {
      handler: function(val, old) {
        // Only trigger if we have a selected group and old value exists (not initial load)
        if (this.selectedGroup && old !== undefined) {
          // Wait for next tick to ensure saveGroups() from the caller has completed
          this.$nextTick(() => {
            this.fetchTypeCurves()
            this.fetchStatistics()
          })
        }
      },
      deep: true
    },
    "ratios.selected": function() {
      this.fetchRatios()
    },
    dimension: function (val, old) {
      if (this.item) {
        this.fetchProbit()
        this.fetchStatistics()
        this.statistics.headers = this.statistics.headers.filter((h) => h.value !== "dimension")
        if (val) {
          if (val == "Start Date") {
            this.statistics.headers.unshift({ text: "START YEAR", value: "dimension" })
          } else {
            this.statistics.headers.unshift({ text: String(val).toUpperCase(), value: "dimension" })
          }
        }
      }
    },
    isDark: function (val) {
      this.toggleDarkMode(val)
    },
    showProbit: function(val) {
      if (!!val) {
        this.fetchProbit()
      } else {
        this.charts.probit.data = []
      }
    },
    showRatios: function(val) {
      if (!!val) {
        this.fetchRatios()
      } else {
        this.charts.ratio.data = []
      }
    },
  },
  methods: {
    plotTypeCurves() {
      this.charts.aggregates.data = []
      let timeline = this.type_curves.aggregates["timeline"]

      Object.entries(this.type_curves.aggregates).forEach(([text, curve]) => {

        let values = Object.values(curve)
        let max_months = Math.max(values.length, this.months, this.termination.duration.max)
        let min_months = Math.min(values.length, this.months, this.termination.duration.max)
        let x_axis = []
        if (this.timescale.selected == 'absolute') {
          x_axis = timeline.slice(0, this.showTermination ? max_months : min_months)
          this.charts.aggregates.layout.xaxis = { title: { text: `Time (months)` }, dtick: "M12" }
        } else {
          x_axis = this.create_array(1, this.showTermination ? max_months : min_months)
          this.charts.aggregates.layout.xaxis = { title: { text: `Time (months)` }, dtick: 12 }
        }

        let y_axis = this.calculations.selected == "actual" ? values : this.cumulative(values)

        if (["mean", "sum"].includes(text)) {
          this.charts.aggregates.data.push({
            name: this.capitalize(text), type: "bar", showlegend: true, x: x_axis, y: y_axis,
            marker: { color: this.targets.options.find((t) => t.value == this.targets.selected).color, opacity: 0.5 },
          })
        } else if (["p10", "p50", "p90"].includes(text)) {
          this.charts.aggregates.data.push({
            name: this.capitalize(text), type: "scatter", showlegend: true, x: x_axis, y: y_axis,
            line: { color: this.colors.curves[text], dash: "solid", width: 1 },
          })
        }
      })

      let curves = this.groupCurves
      curves = this.isEmpty(curves) ? this.declinedCurves : curves
      if (!this.isEmpty(curves)) {
        // Add declined curves -- the Mean fit exists only to be exported,
        // so it's never drawn.
        Object.entries(curves).filter(([key]) => key !== "Mean").forEach(([key, curve]) => {
          this.charts.aggregates.data.push({
            name: key,
            type: "scatter",
            showlegend: false,
            x: this.create_array(1, this.showTermination ? this.termination.duration.max : this.months),
            y: curve,
            line: { color: this.colors.curves[key.toLowerCase()], dash: "dot", width: 2 },
          })
        })
      }

      // Plot imported curves (if any)
      let imported = []
      if (this.isObject(this.type_curves.imported)) {
        let data = this.type_curves.imported
        const months = [...Array(this.termination.duration.max).keys()].map((x) => x + 1)
        imported = months.map((month) => {
          return F.arps(data.Q, data.D, data.B, month)
        })
      } else if (Array.isArray(this.type_curves.imported)) {
        imported = this.type_curves.imported
      }
      if (!this.isEmpty(imported)) {
        this.charts.aggregates.data.push({
          name: "Imported",
          type: "scatter",
          y: imported,
          line: { color: this.colors.curves['avg'], dash: "dot", width: 2 },
        })
      }
    },
    /**
     * Save declined curves or parameters to the selected group
     * @param {string} mode Can be 'params', 'curves', or 'termination'
     * @param {Object} data Contains P10, P50, P90 curves/parameters or termination settings
     * 
     * Type curve workflow: params + curves + termination coexist (decline → params, terminate → curves + termination)
     * Well aggregate workflow: only termination (mutually exclusive with params/curves)
     */
    saveDeclinedCurves(mode, data) {
      if (!(this.targets.selected in this.selectedGroup.targets)) {
        this.$set(this.selectedGroup.targets, this.targets.selected, { enabled: true })
      }
      
      // If saving termination settings for well aggregates, clear params/curves (type curves)
      // But allow termination to coexist with params/curves for type curve termination
      if (mode === 'termination' && !this.hasParams) {
        this.$delete(this.selectedGroup.targets[this.targets.selected], 'params')
        this.$delete(this.selectedGroup.targets[this.targets.selected], 'curves')
      }
      
      // If saving params (decline operation), clear well-aggregate termination
      if (mode === 'params') {
        const hasTerminationOnly = this.hasTermination && !this.hasParams && !this.hasCurves
        if (hasTerminationOnly) {
          this.$delete(this.selectedGroup.targets[this.targets.selected], 'termination')
        }
      }
      
      this.$set(this.selectedGroup.targets[this.targets.selected], mode, data)
      this.saveGroups()
    },
    plotProbit() {
      const method = this.methods.selected
      const target = this.targets.selected

      this.charts.probit.data = []

      // Multiple plots
      if (this.dimension !== undefined) {
        let dimensions = []
        if (this.dimension in this.filters) {
          dimensions = this.filters[this.dimension].list
        } else {
          dimensions = this.methods.options.map((m) => m.value)
        }

        // Split by dimension
        dimensions.forEach((dimension) => {
          let indexes = this.probit[this.dimension].map((e, i) => (e === dimension ? i : undefined)).filter((x) => x)
          let xs = this.probit[target].filter((el, i) => indexes.some((j) => i === j))
          let ys = this.probit["rank"].filter((el, i) => indexes.some((j) => i === j))
          let labels = this.probit[this.API].filter((el, i) => indexes.some((j) => i === j))

          this.charts.probit.data.push({
            name: String(dimension).toUpperCase(),
            type: "scatter",
            mode: "markers",
            // showlegend: false,
            x: xs,
            y: ys,
            text: labels,
          })
        })
      } else {
        // Single probit plot
        this.charts.probit.data.push({
          name: "Probit",
          type: "scatter",
          mode: "markers",
          // showlegend: false,
          x: this.probit[target],
          y: this.probit["rank"],
          text: this.probit[this.API],
          marker: { size: 6, color: this.targets.options.find((t) => t.value === this.targets.selected).color },
          selected: { marker: { size: 12, color: "#F44336" } },
        })
      }
      // Plot selected well (if selected)
      // if (!!this.selectedWell) {
      //   // Add to probit layer
      //   let index = this.charts.probit.data[0].text.findIndex(well => well == this.selectedWell)
      //   this.charts.probit.data.push({
      //     name: this.selectedWell,
      //     type: "scatter",
      //     mode: "markers",
      //     x: [this.charts.probit.data[0].x[index]],
      //     y: [this.charts.probit.data[0].y[index]],
      //     marker: { size: 12, color: "#F44336" }
      //   })
      // }
      this.charts.probit.layout.xaxis.title.text = this.targetUnits
    },
    plotRatios() {
      this.charts.ratio.data = []
      Object.entries(this.ratios.data).forEach(([target, data]) => {
        let line = this.targets.options.filter((o) => o.value == target)
        let max_months = Math.max(data.length, this.months, this.termination.duration.max)
        let min_months = Math.min(data.length, this.months, this.termination.duration.max)
        this.charts.ratio.data.push({
          name: target == "ratio" ? String(this.ratios.selected).toUpperCase() : String(target).toUpperCase(),
          type: "scatter",
          x: this.create_array(1, this.showTermination ? max_months : min_months),
          y: data,
          yaxis: target == "ratio" ? "y2" : "y1",
          line: {
            color: line ? line.color : "red",
            dash: target == "ratio" ? "dot" : "solid",
            width: target == "ratio" ? 2 : 1,
          },
        })
      })
    },
    onProbitClick(event) {
      if (event) {
        let well = event.points[0].text
        if (well == this.selectedWell) {
          this.selectedWell = null
        } else {
          this.selectedWell = well
        }
      }
    },
    onProbitSelect(event) {
      if (event) {
        if (!this.isEmpty(event.points)) {
          this.fetchAggregates()
        }
      }
    },
    plotSingleWell() {
      let target = this.targets.selected
      let method = this.methods.selected

      // Reset existing data
      this.charts.single.data = []

      let production = this.well.production
      let index = this.last_valid_index(production)

      // Populate production
      this.charts.single.data.push({
        name: "Production",
        type: "bar",
        x: this.create_array(1, this.well.production.length + 24),
        y: this.calculations.selected == "cumulative" ? this.cumulative(production) : production,
        marker: { color: this.targets.options.find((t) => t.value == target).color, opacity: 0.5 },
      })

      this.item.methods.forEach((m) => {
        let name = m.slug
        // Populate predictions
        let prediction = this.well.predictions[name].filter(x => x > 0)
        this.charts.single.data.push({
          name: `Prediction ${name}`,
          type: "scatter",
          mode: "lines",
          x: this.create_array(index + 1, index + prediction.length),
          y: this.calculations.selected == "cumulative" ? this.cumulative(prediction) : prediction,
          line: { color: this.colors.lines[name], shape: "spline", dash: "dot", width: 1 },
        })

        // Populate estimations
        let decline = this.well.declines[name].slice()  // Create a copy to avoid mutating original data
        // Hide curves from beginning to EOL
        decline = Array(index + prediction.length)
          .fill(null)
          .concat(decline.slice(index + prediction.length))
        this.charts.single.data.push({
          name: `Decline ${name}`,
          type: "scatter",
          mode: "lines",
          x: this.showTermination ? this.create_array(1, this.termination.duration.max) : this.create_array(1, index + 24),
          y: this.calculations.selected == "cumulative" ? this.cumulative(decline) : decline,
          line: { color: this.colors.lines[name], shape: "spline", dash: "solid", width: 1 },
        })
      })

      if (this.hasParams) {
        this.charts.single.data.push({
          name: "P50",
          type: "scatter",
          x: this.create_array(1, this.showTermination ? this.termination.duration.max : this.months + 24),
          y: this.declinedCurves["P50"],
          line: { color: this.colors.curves["p50"], dash: "dot", width: 1 },
        })
      }
    },
    onDownload() {
      Forecasting.get_downloads(this.item.uuid, this.requestPayload()).then((response) => {
        let csv = response.data
        var downloadLink = document.createElement("a")
        var blob = new Blob(["\ufeff", csv])
        var url = URL.createObjectURL(blob)
        downloadLink.href = url
        downloadLink.download = this.groups.selected ? `${this.selectedGroup.name}_${this.item.name}.csv` : `${this.item.name}.csv` //Name the file here
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
      })
    },
    limitArray(input, target, method) {
      if (this.isEmpty(input)) return input
      let output = input

      let limit = input.length
      if (this.termination.method == "duration") {
        limit = this.termination.duration.value
      } else if (this.termination.method == "production") {
        let prod = input.findIndex((x) => x !== null && x < this.termination.production.value)
        limit = prod <= 0 ? input.length : prod
      } else if (this.termination.method == "decline") {
        limit = this.limitDecline(input, target, method)
      }

      output = input.slice(0, limit)
      return output
    },
    limitDecline(input, target, method) {
      if (this.isEmpty(input)) return input

      var Di = undefined,
        b = undefined

      if (!!this.selectedWell) {
        let parameters = this.wellParams
        Di = parameters[`Di_${method}_${target}`]
        b = parameters[`b_${method}_${target}`]
      } else {
        if (!this.isEmpty(this.type_curves.declines)) {
          Di = this.type_curves.parameters[target]["P50"]["Di"]
          b = this.type_curves.parameters[target]["P50"]["b"]
        } else {
          return input.length
        }
      }

      if (Di == undefined && b == undefined) return input

      const declined = input.map((a, i) => 12 * Math.log(input[i - 1] / input[i]))
      const limit = this.termination.decline.value / 100
      let index = declined.findIndex((x) => x !== null && x < limit)
      index = Math.max(index, this.months + 12)
      return index
    },
    extendArray(input, key = null) {
      if (this.isEmpty(input)) return input
      if (input.length == this.termination.duration.max) return input

      // const declines = input.map((a,i) => 12 * Math.log(input[i-1] / input[i]))
      let limit = this.termination.decline.value / 10
      // const index = declines.findIndex(x => x !== null && x < limit)
      // const start = input.slice(0, index)

      for (let i = input.length; i < this.termination.duration.max; i++) {
        input.push(input[input.length - 1] * Math.exp(-(limit / 12)))
      }

      limit = input.findIndex((x) => x !== null && x < 1)

      return input.slice(0, limit)
    },
    /**
     * Handle termination for type curves (hasParams=true)
     * Terminates theoretical P10/P50/P90 curves and returns terminated curve arrays
     */
    onCurveTermination(data) {
      this.termination = data
      this.fetchTerminalDeclines()
    },
    /**
     * Handle termination for well aggregates (hasParams=false)
     * Terminates individual wells and returns aggregated production curves
     */
    onWellTermination(data) {
      this.termination = data
      let termination = {
        "method": this.termination.method,
        "value": this.termination[this.termination.method].value,
      }
      
      // Save termination to group and fetch type curves (which will include termination)
      this.$set(this.selectedGroup.targets[this.targets.selected], 'termination', termination)
      this.saveGroups()
      this.fetchTypeCurves()
      this.fetchStatistics()
    },
    onCreateGroup(wells = []) {
      // Use provided wells if valid array with items, otherwise use all filtered wells
      let group = undefined
      let counter = Math.max(this.groups.list.length + 1, ...this.groups.list.map((g) => g.id + 1))

      if (!this.selectedGroup) {
        const selectedWells = (Array.isArray(wells) && wells.length > 0) 
          ? wells 
          : this.table.data.map(w => w[this.API])
        
        group = {
          id: counter,
          name: `Group ${counter}`,
          settings: this.requestSettings(),
          filters: this.finalFilters(),
          wells: selectedWells,
          count: selectedWells.length,
          targets: {},
          locked: false,
          saved: false,
          control_wells: []
        }
        this.targets.options.forEach((t) => {
          this.$set(group.targets, t.value, { enabled: true })
        })
      } else {
        group = { ...this.selectedGroup }
        group.id = counter
        group.name = `Group ${counter}`
        console.log(group)
      }
      this.groups.list.push(group)
      this.groups.selected = counter
      this.saveGroups()
    },
    // Remove well from selected group well list
    // Create well list in selected group if list is empty
    onRemoveWell() {
      this.selectedGroup.wells = this.table.data.filter((well) => well[this.API] !== this.selectedWell).map(w => w[this.API])
      this.selectedWell = undefined
      this.saveGroups()
      this.fetchAggregates()
    },
    onToggleControlWell() {
      if (!this.selectedGroup) return
      
      // Initialize control_wells array if it doesn't exist
      if (!('control_wells' in this.selectedGroup)) {
        this.$set(this.selectedGroup, 'control_wells', [])
      }
      
      // Ensure it's an array
      if (!Array.isArray(this.selectedGroup.control_wells)) {
        this.$set(this.selectedGroup, 'control_wells', [])
      }
      
      // Toggle: add or remove from array
      const index = this.selectedGroup.control_wells.indexOf(this.selectedWell)
      if (index > -1) {
        // Remove from array
        this.selectedGroup.control_wells.splice(index, 1)
      } else {
        // Add to array
        this.selectedGroup.control_wells.push(this.selectedWell)
      }
      
      this.saveGroups()
    },
    onGroupLocked(group) {
      // Capture current filtered wells when group is locked
      if (group.locked && this.table.data.length > 0) {
        group.wells = this.table.data.map((w) => String(w[this.API]))
      }
    },
    onImportWells(wells) {
      this.onCreateGroup(wells)
    },
    onDeclineTypeCurves(range) {
      let formData = {}
      this.type_curves.options.forEach((l) => {
        formData[l.text] = this.charts.aggregates.data.find((c) => c.name == l.text).y.slice(range[0] - 1, range[1])
      })
      // The aggregate is only a true mean in relative timescale (absolute
      // shows a sum, named "Sum"), so there's nothing to fit otherwise.
      const mean = this.charts.aggregates.data.find((c) => c.name == "Mean")
      if (mean) formData["Mean"] = mean.y.slice(range[0] - 1, range[1])
      Forecasting.get_decline(this.item.uuid, formData).then((response) => {
        this.saveDeclinedCurves('params', response.data)
        this.plotTypeCurves()
      })
    },
    onImportTypeCurves(data) {
      this.type_curves.imported = data
      this.plotTypeCurves()
    },
    onEditTypeCurves(params) {
      this.saveDeclinedCurves('params', params)
      this.plotTypeCurves()
    },
    onExportTypeCurves(data) {
      if (data.method == "excel") {
        this.onExportforExcel(data.config)
      }
      if (data.method == "aries") {
        this.onExportForAriesTypeCurve(data.config)
      }
      if (data.method == "combocurve") {
        this.onExportForComboCurve(data.config)
      }
    },
    onExportForAriesTypeCurve(config) {
      // ARIES' type curve library shape: one row per curve (P10/P50/P90/
      // Mean), named by TYPE_CURVE, with that phase's own Qi/b/Di.
      // Client-side only, same as the ComboCurve export above.
      const round = (x, n) => Math.round(x * 10 ** n) / 10 ** n

      const target = config.target
      const targetParams = this.selectedGroup && this.selectedGroup.targets[target]
        ? this.selectedGroup.targets[target].params
        : null
      if (this.isEmpty(targetParams)) {
        this.notify(`No declined type curve found for: ${target} -- nothing to export.`)
        return
      }

      // TYPE_CURVE is a database key -- no spaces or punctuation.
      const sanitize = (s) => String(s || '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '')
      const baseName = sanitize(config.name)
      if (!baseName) {
        this.notify('Name must contain at least one letter or number.')
        return
      }

      const settings = (this.selectedGroup && this.selectedGroup.settings) || {}
      const qualifierAbbr = deriveMethodTechniqueAbbr(settings.method, settings.technique) || 'UNKNOWN'

      const headers = ['TYPE_CURVE', `${target.toUpperCase()}_QI`, `${target.toUpperCase()}_B`, `${target.toUpperCase()}_DI`]
      const typeRow = ['Match', 'Numeric', 'Text', 'Numeric']

      const rows = []
      const missing = []
      config.curves.forEach((curve) => {
        const params = targetParams[curve]
        if (!params) {
          missing.push(curve)
          return
        }
        const { Q, D, B } = params
        // Di Eff-Sec % -- same secant-effective convention as every
        // other D value this app hands to ARIES.
        const diPct = F.effectiveSecDecline(D, B, 12) * 100
        rows.push([
          `${baseName}_${curve.toUpperCase()}_${qualifierAbbr}`,
          round(Q, 4),
          `B/${round(B, 4)}`,
          round(diPct, 2),
        ])
      })

      if (!this.isEmpty(missing)) {
        this.notify(`No declined type curve found for: ${missing.join(', ')} -- skipped.`)
      }
      if (this.isEmpty(rows)) {
        return
      }

      var wb = XLSX.utils.book_new()
      var mainWs = XLSX.utils.aoa_to_sheet([headers, typeRow, ...rows])
      XLSX.utils.book_append_sheet(wb, mainWs, 'Type Curve')

      // Reference only -- the wells this type curve was declined from.
      let wells = this.finalWells()
      if (this.isEmpty(wells)) {
        wells = this.wells.map(x => x[this.API])
      }
      var apiWs = XLSX.utils.aoa_to_sheet([['API'], ...wells.map(w => [w])])
      XLSX.utils.book_append_sheet(wb, apiWs, 'APIs')

      var filename = this.item.uuid
      filename += this.groups.selected ? ` - ${this.selectedGroup.name}` : ""
      filename += ` - Type Curve - ARIES.xlsx`
      XLSX.writeFile(wb, filename, { bookType: 'xlsx' })
    },
    onExportForComboCurve(config) {
      // Single segment per curve only -- no terminal-decline switch, so
      // this stays client-side instead of round-tripping to the
      // backend's segment-building machinery, which only earns its keep
      // once a switch is involved.
      const DAYS_PER_MONTH = 30.4375
      const DAYS_PER_YEAR = 365.25
      const round = (x, n) => Math.round(x * 10 ** n) / 10 ** n

      const headers = [
        "TC Name", "TC ID", "Rep Well INPT ID", "Rep Well List Chosen ID", "Phase",
        "EUR Normalization Type", "Peak rate Normalization Type",
        // ComboCurve's own regression-fit columns -- this app never fits
        // EUR/peak-rate-vs-something regressions, only Q/D/b per curve, so
        // these are always blank. Present only so the file matches
        // ComboCurve's own import schema (CC_TC_Export_Sample_From_Grant.csv).
        "EUR Slope", "EUR Intercept", "EUR Coefficient", "EUR Exponent",
        "Peak rate Slope", "Peak rate Intercept", "Peak rate Coefficient", "Peak rate Exponent",
        "x Axis", "y Axis",
        "Regression Type", "TC Life (Year)", "Align Peak",
        "Normalization", "Resolution", "EUR (MBBL,MMCF)", "Fit Type", "Base Phase",
        "Series", "Segment", "Segment Type", "Start of Period (Day)", "End of Period (Day)",
        "q Start (BBL/D, MCF/D, BBL/MCF, MCF/BBL, BBL/BBL)", "q End (BBL/D, MCF/D, BBL/MCF, MCF/BBL, BBL/BBL)",
        "Di Eff-Sec %", "Di Nominal", "b", "Realized D Sw-Eff-Sec %",
      ]

      // Classified from the actual start/end rates, same principle as the
      // main ComboCurve well exporter's _cc_segment_type() -- never
      // assumed from b alone.
      const segmentType = (qStart, qEnd, b) => {
        let direction = 'flat'
        if (qEnd > qStart * (1 + 1e-6)) direction = 'incline'
        else if (qEnd < qStart * (1 - 1e-6)) direction = 'decline'
        if (direction == 'flat') return 'flat'
        if (direction == 'incline') return b < 0.05 ? 'exp_inc' : 'arps_inc'
        return b < 0.05 ? 'exp_dec' : 'arps'
      }

      const lifeMonths = this.termination.duration.value
      const lifeDays = lifeMonths * DAYS_PER_MONTH

      // Rep Well List Chosen ID: the group's wells, as API 10 or API 14 --
      // same idea as the segment exporter's well_id_format. A real API14
      // (sourced from the map data, present only when the dataset has
      // one) is used as-is; otherwise it's derived from the API the same
      // way the backend does (API10 + "0000").
      let apis = this.table.data.map((w) => String(w[this.API]))
      if (this.isEmpty(apis) && this.selectedGroup && this.selectedGroup.wells) {
        apis = this.selectedGroup.wells.map(String)
      }
      const realApi14 = {}
      this.wells.forEach((w) => {
        const value = w.API14 != null ? String(w.API14) : null
        if (value && !["0", "null", "undefined", "nan"].includes(value)) realApi14[String(w[this.API])] = value
      })
      const chosenId = (api) => {
        if (config.well_id_format == "API 14") return realApi14[api] || api.padEnd(14, "0").slice(0, 14)
        return api.slice(0, 10)
      }
      let chosenIds = apis.map(chosenId).join(", ")
      // Excel cells top out at 32,767 characters.
      if (chosenIds.length > 32767) {
        this.notify("Too many wells to list in one cell -- Rep Well List Chosen ID left blank.")
        chosenIds = ""
      }

      // Unlike Rep Well List Chosen ID, TC Name repeats on every row of the
      // file (matches the reference sample) -- it names the whole type
      // curve, not a per-row well list.
      const tcName = (config.tc_name && config.tc_name.trim()) || `${this.selectedGroup.name} TC`

      // Params are only ever populated for a target once the user has run
      // "Decline Type Curves" while THAT target was selected in the main
      // UI -- applying a type curve to a group does not fit oil/gas/water
      // simultaneously, so a target picked here can easily have nothing
      // to export yet.
      const rows = []
      const missing = []
      // The well list appears once, on the file's first row only.
      let firstRowOverall = true
      const targets = !this.isEmpty(config.targets) ? config.targets : [config.target]
      targets.forEach((target) => {
        const targetParams = this.selectedGroup && this.selectedGroup.targets[target]
          ? this.selectedGroup.targets[target].params
          : null
        if (this.isEmpty(targetParams)) {
          missing.push(target)
          return
        }

        // ComboCurve only states the normalization types on the first row
        // of each phase; every later row for that phase leaves them blank.
        let firstRowOfPhase = true
        // ComboCurve calls the fit through the average data "best" and
        // lists it first in each phase; our Mean fit is that series.
        const orderedCurves = [...config.curves].sort((a, b) => (b == "Mean") - (a == "Mean"))
        // This app's P10/P90 are statistical quantiles (P10 = the 0.1
        // quantile, the LOW curve; P90 = the 0.9 quantile, the HIGH curve)
        // -- confirmed throughout the app (type_curves.options etc). The
        // reference sample uses the reserves convention instead, where
        // P10 is the HIGH case and P90 the LOW case (its own EUR values
        // go P10 > P50 > P90). Only the exported label is swapped here --
        // the internal P10/P90 keys/values are untouched everywhere else.
        const seriesLabel = (curve) => {
          if (curve == "Mean") return "best"
          if (curve == "P10") return "P90"
          if (curve == "P90") return "P10"
          return curve
        }
        orderedCurves.forEach((curve) => {
          const params = targetParams[curve]
          if (!params) return
          const { Q, D, B } = params
          const normalizationType = firstRowOfPhase ? "no_normalization" : ""
          firstRowOfPhase = false
          const chosenIdCell = firstRowOverall ? chosenIds : ""
          firstRowOverall = false

          // Q/D come off the same monthly-nominal basis as every other
          // fit in this app -- converted to daily here since that's the
          // convention this export's own q/Di columns use.
          const qStartDaily = Q / DAYS_PER_MONTH
          const qEndDaily = F.arps(Q, D, B, lifeMonths) / DAYS_PER_MONTH
          const dNominalDaily = D / DAYS_PER_MONTH
          const dEffPct = F.effectiveSecDecline(dNominalDaily, B, DAYS_PER_YEAR) * 100
          const eurValue = F.eur(Q, D, B, lifeMonths) / 1000

          rows.push([
            tcName,                            // TC Name -- same on every row
            "", "",                            // TC ID / Rep Well INPT ID -- user fills in
            chosenIdCell,                      // Rep Well List Chosen ID -- first row of the file only
            target,                            // Phase
            normalizationType, normalizationType, // EUR / Peak rate Normalization Type -- first row of each phase only
            "", "", "", "",                     // EUR Slope / Intercept / Coefficient / Exponent -- not fit by this app
            "", "", "", "",                     // Peak rate Slope / Intercept / Coefficient / Exponent -- same
            "", "",                             // x Axis / y Axis -- blank for a production-vs-time type curve
            "Rate",                             // Regression Type -- matches the reference sample
            round(lifeMonths / 12, 2),          // TC Life (Year)
            "align",                            // Align Peak -- matches the reference sample
            "No",                               // Normalization -- matches the reference sample (no-normalization type curve)
            "monthly",                          // Resolution -- matches the reference sample
            round(eurValue, 3),                 // EUR (MBBL,MMCF)
            "rate",                             // Fit Type -- matches the reference sample
            "N/A",                              // Base Phase -- matches the reference sample
            seriesLabel(curve),                 // Series -- ComboCurve's naming/reserves convention, not this app's own
            1,                                  // Segment
            segmentType(qStartDaily, qEndDaily, B), // Segment Type
            0,                                  // Start of Period (Day)
            Math.round(lifeDays),               // End of Period (Day) -- ComboCurve expects an integer day count
            // 6/8 decimals match the backend exporter's own precision --
            // Di Nominal gets multiplied by thousands of days before being
            // exponentiated, so rounding it any coarser visibly throws off
            // q End relative to q Start (verified: 4/5 decimals produced a
            // ~0.2-0.6% mismatch reconstructing q End from the exported
            // Di Nominal; 6/8 brings that under 0.001%).
            round(qStartDaily, 6),              // q Start
            round(qEndDaily, 6),                // q End
            round(dEffPct, 2),                  // Di Eff-Sec %
            round(dNominalDaily, 8),            // Di Nominal
            B,                                  // b
            "",                                 // Realized D Sw-Eff-Sec % -- no switch, single segment
          ])
        })
      })

      if (!this.isEmpty(missing)) {
        this.notify(`No declined type curve found for: ${missing.join(', ')} -- skipped.`)
      }
      if (this.isEmpty(rows)) {
        return
      }

      var wb = XLSX.utils.book_new()
      var cws = XLSX.utils.aoa_to_sheet([[...headers], ...rows])
      XLSX.utils.book_append_sheet(wb, cws, "Data")

      var filename = this.item.uuid
      filename += this.groups.selected ? ` - ${this.selectedGroup.name}` : ""
      filename += ` - Type Curves - ComboCurve.csv`
      XLSX.writeFile(wb, filename, { bookType: "csv" })
    },
    onExportforExcel(config) {
      var wb = XLSX.utils.book_new()

      if (!this.isEmpty(this.type_curves.curves)) {
        // TAB1 - Volumes
        const months = Math.max(this.termination.duration.max, this.months)
        var curves = {}
        curves['Date'] = Object.keys(this.type_curves.aggregates['mean']).map(e => parseInt(e))
        this.filteredScalars.forEach((scalar) => {
          var value = this.mean(this.table.data.map(w => w[scalar == this.LATLEN ? "Length" : scalar]))
          curves[scalar] = this.create_array(1, months).map(() => value)
        })
        config.curves.forEach((curve) => {
          curves[curve] = this.type_curves.curves[curve].filter((n) => n)
        })
        if (!!config.production) {
          curves["Mean"] = Object.values(this.type_curves.aggregates.mean).filter((n) => n)
        }

        var cdf = new DataFrame(curves)
        var tab1 = [cdf.listColumns(), ...cdf.toArray()]
        var cws = XLSX.utils.aoa_to_sheet(tab1)
        XLSX.utils.book_append_sheet(wb, cws, "Volumes")

        // TAB2 - Parameters
        // if (!!config.parameters) {
        var params = {
          Parameter: ["q", "d", "b"],
          P10: [this.type_curves.parameters.P10.Q, this.type_curves.parameters.P10.D, this.type_curves.parameters.P10.B],
          P50: [this.type_curves.parameters.P50.Q, this.type_curves.parameters.P50.D, this.type_curves.parameters.P50.B],
          P90: [this.type_curves.parameters.P90.Q, this.type_curves.parameters.P90.D, this.type_curves.parameters.P90.B],
        }
        var pdf = new DataFrame(params)
        var tab2 = [pdf.listColumns(), ...pdf.toArray()]
        var pws = XLSX.utils.aoa_to_sheet(tab2)
        XLSX.utils.book_append_sheet(wb, pws, "Parameters")
        // }

        // TAB3 - Summary Stats
        // if (!!config.statistics) {
        var sdf = new DataFrame(this.statistics.items)
        var tab3 = [sdf.listColumns(), ...sdf.toArray()]
        var sws = XLSX.utils.aoa_to_sheet(tab3)
        XLSX.utils.book_append_sheet(wb, sws, "Statistics")
        // }

        // TAB4 - List of APIs
        // if (!!config.identities) {
        var wells = this.finalWells()
        if (this.isEmpty(wells)) {
          wells = this.wells.map(x => x[this.API])
        }
        var adf = new DataFrame({ API: wells })
        var tab4 = [adf.listColumns(), ...adf.toArray()]
        var aws = XLSX.utils.aoa_to_sheet(tab4)
        XLSX.utils.book_append_sheet(wb, aws, "APIs")
        // }

        var filename = this.item.uuid
        filename += this.groups.selected ? ` - ${this.selectedGroup.name}` : ""
        filename += ` - Type Curves.xlsb`
        // Return binary XLS format (smaller)
        XLSX.writeFile(wb, filename)
      } else {
        // TAB1 - Volumes
        const months = Math.max(this.termination.duration.max, this.months)
        var curves = {}
        curves['Date'] = Object.keys(this.type_curves.aggregates['mean']).map(e => parseInt(e))
        this.filteredScalars.forEach((scalar) => {
          var value = this.mean(this.table.data.map(w => w[scalar == this.LATLEN ? "Length" : scalar]))
          curves[scalar] = this.create_array(1, months).map(() => value)
        })
        config.curves.forEach((curve) => {
          curves[curve] = Object.values(this.type_curves.aggregates[curve.toLowerCase()]).filter((n) => n)
        })
        if (!!config.production) {
          curves["Mean"] = Object.values(this.type_curves.aggregates.mean).filter((n) => n)
        }
        var cdf = new DataFrame(curves)
        var tab1 = [cdf.listColumns(), ...cdf.toArray()]
        var cws = XLSX.utils.aoa_to_sheet(tab1)
        XLSX.utils.book_append_sheet(wb, cws, "Volumes")

        // TAB3 - Summary Stats
        var sdf = new DataFrame(this.statistics.items)
        var tab3 = [sdf.listColumns(), ...sdf.toArray()]
        var sws = XLSX.utils.aoa_to_sheet(tab3)
        XLSX.utils.book_append_sheet(wb, sws, "Statistics")

        // TAB4 - List of APIs
        var wells = this.finalWells()
        if (this.isEmpty(wells)) {
          wells = this.wells.map(x => x[this.API])
        }
        var adf = new DataFrame({ API: wells })
        var tab4 = [adf.listColumns(), ...adf.toArray()]
        var aws = XLSX.utils.aoa_to_sheet(tab4)
        XLSX.utils.book_append_sheet(wb, aws, "APIs")

        var filename = this.item.uuid
        filename += this.groups.selected ? ` - ${this.selectedGroup.name}` : ""
        filename += ` - Type Curves.xlsb`
        // Return binary XLS format (smaller)
        XLSX.writeFile(wb, filename)
      }
    },
    onExportEconomics() {
    },
    onClearDeclines() {
      // Clear data
      this.type_curves.import = null
      this.termination.method = "decline"
      // Remove previous curves from chart
      this.charts.aggregates.data = this.charts.aggregates.data.filter((c) => c.showlegend)
      // Clear all type curve data (params, curves, and termination settings) from selected group
      this.$delete(this.selectedGroup.targets[this.targets.selected], 'params')
      this.$delete(this.selectedGroup.targets[this.targets.selected], 'curves')
      this.$delete(this.selectedGroup.targets[this.targets.selected], 'termination')
      this.saveGroups()
      this.fetchAggregates()
    },
    onMenuChange(value, variable) {
      this.setPath(this, variable, value)
    },
    selectPointsOn(plot, wells = undefined) {
      if (!this.isEmpty(this.charts[plot].data)) {
        if ("selectedpoints" in this.charts[plot].data[0]) {
          this.$delete(this.charts[plot].data[0], "selectedpoints")
        }
        if (!!wells) {
          let indexes = this.charts[plot].data[0].text.map((e, i) => (wells.includes(e) ? i : "")).filter(String)
          this.$set(this.charts[plot].data[0], "selectedpoints", indexes)
        }
      }
    },
    onEditWell(params) {
      Predictions.set_well(this.item.uuid, params).then(_ => {
        this.fetchWellData()
      })
    },
    onNearbyWells(options) {
      let wells = []
      const well = this.wells.find(x => x[this.API] == this.selectedWell)
      const lat = well[this.item.config.latitude]
      const lon = well[this.item.config.longitude]

      this.wells.forEach(well => {
        let distance = F.getDistanceFromLatLon(
          lat,
          lon,
          well[this.item.config.latitude],
          well[this.item.config.longitude],
          options.units
        )
        if (distance <= options.radius) {
          wells.push(well[this.API])
        }
      })
      this.onCreateGroup(wells)
    },
    onExportSingleWell() {
      var wb = XLSX.utils.book_new()
      var data = {
        production: this.well.production,
        prediction: this.well.predictions[this.methods.selected],
        decline: this.well.declines[this.methods.selected],
      }
      var df = new DataFrame(data)
      var tab = [df.listColumns(), ...df.toArray()]
      var ws = XLSX.utils.aoa_to_sheet(tab)
      XLSX.utils.book_append_sheet(wb, ws, this.targets.selected)
      let filename = `${this.item.uuid} - ${this.selectedWell}.xlsb`
      XLSX.writeFile(wb, filename)
    },
    toggleDarkMode(val) {
      if (val) {
        this.$set(this.charts.aggregates.layout.yaxis, "gridcolor", "#111")
        this.$set(this.charts.aggregates.layout.xaxis, "gridcolor", "#111")
        this.$set(this.charts.probit.layout.yaxis, "gridcolor", "#111")
        this.$set(this.charts.probit.layout.xaxis, "gridcolor", "#111")
        this.$set(this.charts.single.layout.xaxis, "gridcolor", "#111")
        this.$set(this.charts.single.layout.yaxis, "gridcolor", "#111")
        this.$set(this.charts.ratio.layout.xaxis, "gridcolor", "#111")
        this.$set(this.charts.ratio.layout.yaxis, "gridcolor", "#111")
      } else {
        this.$set(this.charts.aggregates.layout.yaxis, "gridcolor", "#eee")
        this.$set(this.charts.aggregates.layout.xaxis, "gridcolor", "#eee")
        this.$set(this.charts.probit.layout.yaxis, "gridcolor", "#eee")
        this.$set(this.charts.probit.layout.xaxis, "gridcolor", "#eee")
        this.$set(this.charts.single.layout.xaxis, "gridcolor", "#eee")
        this.$set(this.charts.single.layout.yaxis, "gridcolor", "#eee")
        this.$set(this.charts.ratio.layout.xaxis, "gridcolor", "#eee")
        this.$set(this.charts.ratio.layout.yaxis, "gridcolor", "#eee")
      }
    },
    finalWells() {
      if (!!this.selectedWell) return [this.selectedWell]
      // If a group is selected and has wells, use them
      if (!!this.selectedGroup && this.selectedGroup.wells && this.selectedGroup.wells.length > 0) {
        return this.selectedGroup.wells
      }
      // Otherwise return empty array (use filters)
      return []
    },
    finalFilters() {
      let years = {}
      let objects = {}
      let numbers = {}

      let o = Object.fromEntries(Object.entries(this.filteredObjects).filter(([k, v]) => v.selected.length > 0))
      for (const [k, v] of Object.entries(o)) {
        if (v.type == "datetime64[ns]") years[k] = v.selected
        else objects[k] = v.selected
      }

      let n = Object.fromEntries(Object.entries(this.filteredNumbers).filter(([k, v]) => v.selected[0] > v.min || v.selected[1] < v.max))
      for (const [k, v] of Object.entries(n)) {
        numbers[k] = v.selected
      }

      let final = {}
      if (!this.isEmpty(years)) final["years"] = years
      if (!this.isEmpty(objects)) final["objects"] = objects
      if (!this.isEmpty(numbers)) final["numbers"] = numbers

      return final
    },
    requestSettings() {
      let settings = {}
      settings["ratio"]     = this.ratios.selected
      settings["target"]    = this.targets.selected
      settings["method"]    = this.methods.selected
      settings["quality"]   = this.quality.selected
      settings["approach"]  = this.approach.selected
      settings["technique"] = this.technique.selected
      settings["timescale"] = this.timescale.selected
      settings["normalize"] = this.normalize.selected
      // settings["operator"] = this.operator.selected

      if (this.scaling.enabled && this.scaling.value) settings["scaling"] = this.scaling.value

      return settings
    },
    requestPayload() {
      let payload = {
        wells: this.finalWells(),
        dimension: this.dimension || null,
        filters: !!this.selectedGroup ? this.selectedGroup.filters : this.finalFilters(),
        settings: this.requestSettings(),
        control_wells: !!this.selectedGroup && this.selectedGroup.control_wells ? this.selectedGroup.control_wells : [],
      }
      
      // Include termination if present in selected group
      if (this.hasTermination) {
        payload.termination = this.selectedGroup.targets[this.targets.selected].termination
      }
      
      return payload
    },
    economicPayload() {
      return {
        targets: this.targets.options.map((t) => t.value),
        settings: this.selectedGroup.settings,
      }
    },
    setFilters() {
      this.clearFilters()

      this.targets.selected = this.selectedGroup.settings.target
      this.methods.selected = this.selectedGroup.settings.method
      this.quality.selected = this.selectedGroup.settings.quality
      this.approach.selected = this.selectedGroup.settings.approach
      this.technique.selected = this.selectedGroup.settings.technique
      this.timescale.selected = this.selectedGroup.settings.timescale
      this.normalize.selected = this.selectedGroup.settings.normalize
      this.scaling.value = this.selectedGroup.settings.scaling
      this.scaling.enabled = this.scaling.value ? true : false

      if ("filters" in this.selectedGroup) {
        if ("objects" in this.selectedGroup.filters) {
          Object.entries(this.selectedGroup.filters.objects).forEach(([k, v]) => {
            this.filters[k].selected = v
          })
        }
        if ("numbers" in this.selectedGroup.filters) {
          Object.entries(this.selectedGroup.filters.numbers).forEach(([k, v]) => {
            this.filters[k].selected = v
          })
        }
        if ("years" in this.selectedGroup.filters) {
          Object.entries(this.selectedGroup.filters.years).forEach(([k, v]) => {
            this.filters[k].selected = v
          })
        }
      }
    },
    clearFilters() {
      Object.entries(this.filteredObjects).forEach(([k, v]) => {
        v.selected = []
      })
      Object.entries(this.filteredNumbers).forEach(([k, v]) => {
        v.selected = [v.min, v.max]
      })

      this.targets.selected = this.targets.options[0].value
      this.methods.selected = this.methods.options[0].value
      this.quality.selected = undefined
      this.approach.selected = this.approach.options[0].value
      this.technique.selected = this.technique.options[0].value
      this.normalize.selected = this.normalize.options[0].value
      // this.operator.selected = this.operator.options[0].value
      this.scaling.enabled = false
    },
    fetchFilters() {
      Forecasting.get_filters(this.item.uuid).then((response) => {
        this.filters = response.data
        for (let [filter, value] of Object.entries(this.filters)) {
          if (["object", "datetime64[ns]"].includes(value.type)) this.filters[filter].selected = []
          else this.filters[filter].selected = [value.min, value.max]
        }
      })
    },
    fetchMapData() {
      Forecasting.get_map_data(this.item.uuid, this.requestPayload()).then((response) => {
        this.wells = response.data
        this.wells = this.wells.map(x => ({
          ...x, [this.API]: String(x[this.API])
        }))
      })
    },
    fetchFrameData() {
      this.loading.frame = true
      Forecasting.get_frame_data(this.item.uuid, this.requestPayload()).then((response) => {
        this.table.data = response.data ? response.data : []
        
        // Update group count based on what backend returned
        if (this.selectedGroup) {
          this.selectedGroup.count = this.table.data.length
          this.saveGroups()
        }
        
        let exclude = [this.item.config.latitude, this.item.config.longitude, 'Quality']
        if (!this.isEmpty(this.table.data)) {
          this.table.headers = Object.keys(this.table.data[0])
            .filter((e) => !exclude.includes(e))
            .map(
              function (e) {
                if (["oil", "gas", "water"].includes(e)) {
                  let option = this.targets.options.find((t) => t.value == e)
                  let units = option ? option.units : ""
                  return { text: `${this.capitalize(e)} (${units})`, value: e, width: 1 }
                }
                return { text: e, value: e, width: 1 }
              }.bind(this)
            )
          this.table.headers.unshift({ text: "", value: "Quality", width: 1 })
          this.table.headers.splice(1, 0, { text: "", value: "Control", width: 1 })
          if (this.hasLateralLength) {
            this.table.headers.forEach((h) => {
              if (h.value == "Length") {
                h.text = this.LATLEN
              }
            })
          }
          // Make the last column expandable
          delete this.table.headers[this.table.headers.length - 1].width
          let wells = this.table.data.map((w) => String(w[this.API]) )
          // this.selectPointsOn("map", wells)
        }
        this.table.selected = []
      }).finally(() => {
        this.loading.frame = false
      })
    },
    // fetchResults() {
    //   Forecasting.get_filtered_wells(this.item.uuid, this.requestPayload()).then((response) => {
    //     this.selectPointsOn("map", response.data.map(String))
    //   })
    // },
    fetchProduction() {
      this.loading.production = true
      Forecasting.get_production(this.item.uuid, this.requestPayload()).then((response) => {
        this.production = response.data
      }).finally(() => {
        this.loading.production = false
      })
    },
    fetchStatistics() {
      this.loading.statistics = true
      Forecasting.get_statistics(this.item.uuid, this.requestPayload()).then((response) => {
        this.statistics.items = response.data
        // Toggle Lateral Length
        this.statistics.headers = this.statistics.headers.filter((h) => h.value != "length")
        if (this.hasLateralLength) {
          this.statistics.headers.push({ text: "Lateral Length", value: "length" })
        }
      }).finally(() => {
        this.loading.statistics = false
      })
    },
    fetchTypeCurves() {
      this.loading.typeCurves = true
      Forecasting.get_type_curves(this.item.uuid, this.requestPayload()).then(response => {
        if (!this.isEmpty(response.data)) {
          this.type_curves.aggregates = response.data
          this.plotTypeCurves()
        } else {
          this.$set(this.charts.aggregates, "data", [])
          this.$set(this.charts.probit, "data", [])
          this.statistics.items = []
        }
      }).finally(() => {
        this.loading.typeCurves = false
      })
    },
    fetchRatios(ratio = null) {
      if (!!this.item) {
        ratio = ratio || this.ratios.selected
        const visible = this.ratios.options.some((o) => o.visible && o.value == ratio)
        if (this.showRatios && this.canComputeRatios && visible) {
          this.loading.ratios = true
          let payload = this.requestPayload()
          payload["settings"]["ratio"] = ratio
          Forecasting.get_ratios(this.item.uuid, payload).then((response) => {
            this.ratios.data = response.data
            this.plotRatios()
          }).finally(() => {
            this.loading.ratios = false
          })
        }
      }
    },
    fetchProbit() {
      if (this.showProbit) {
        this.loading.probit = true
        Forecasting.get_probit(this.item.uuid, this.requestPayload()).then((response) => {
          this.probit = response.data
          this.plotProbit()
        }).finally(() => {
          this.loading.probit = false
        })
      }
    },
    fetchAggregates() {
      // this.fetchResults()
      this.fetchFrameData()
      this.fetchProduction()
      this.fetchStatistics()
      this.fetchTypeCurves()
      this.fetchProbit()
      this.fetchRatios()
    },
    fetchWellData() {
      this.loading.well = true
      Forecasting.get_well_data(this.item.uuid, this.requestPayload()).then((response) => {
        this.well = response.data
        this.plotSingleWell()
      }).finally(() => {
        this.loading.well = false
      })
    },
    fetchTerminalDeclines() {
      let payload = this.requestPayload()
      let termination = {
        "method": this.termination.method,
        "value": this.termination[this.termination.method].value,
      }
      if (!this.termination.individual) {
        termination["params"] = this.selectedGroup.targets[this.targets.selected]['params']
      }
      payload["termination"] = termination
      this.loading.terminalDeclines = true
      Forecasting.get_terminal_declines(this.item.uuid, payload).then((response) => {
        // Save the terminated curves
        this.saveDeclinedCurves('curves', response.data)
        
        // Save the termination settings used for type curves
        const terminationSettings = {
          "method": this.termination.method,
          "value": this.termination[this.termination.method].value,
        }
        this.saveDeclinedCurves('termination', terminationSettings)
        
        this.plotTypeCurves()
      }).finally(() => {
        this.loading.terminalDeclines = false
      })
    },
    saveGroups() {
      let payload = { 'groups': this.clone(this.groups.list) }
      Predictions.patch(this.item.uuid, payload)
    },
    onResetFilters() {
      if (this.groups.selected) {
        this.groups.selected = undefined
      } else {
        // this.selectPointsOn("map")
        this.clearFilters()
        this.table.data = []
        this.table.selected = []
      }
    },
    onChangeFilter() {
      this.selectedWell = null
      if (!!this.selectedGroup) {
        if (!this.selectedGroup.locked) {
          this.selectedGroup.filters = this.finalFilters()
          this.selectedGroup.settings = this.requestSettings()
          this.fetchAggregates()
        }
      }
    },
    onChangeSettings() {
      if (!!this.selectedGroup && !this.selectedWell) {
        this.selectedGroup.settings = this.requestSettings()
        this.fetchAggregates()
        this.saveGroups()
      } else if (!!this.selectedWell) {
        this.fetchWellData()
      } else {
        
      }
    },
    onRowClick(item) {
      if (this.selectionMode === 'single') {
        // Single-select mode: toggle single well selection
        let well = item[this.item.config.api]
        if (this.selectedWell == well) {
          this.selectedWell = null
          this.table.selected = []
        } else {
          this.selectedWell = well
          this.table.selected = [item]
        }
      }
      // In multi-select mode, let the checkbox handle selection
    },
    onToggleSelectionMode(value) {
      // Value is true when switching to multi-select, false for single-select
      this.selectionMode = value ? 'multi' : 'single'
      
      // Clear selected well when switching to multi-select
      if (this.selectionMode === 'multi') {
        this.selectedWell = null
        // Pre-select control wells in the table if they exist
        if (this.selectedGroup && this.selectedGroup.control_wells) {
          this.table.selected = this.table.data.filter(item => 
            this.selectedGroup.control_wells.includes(String(item[this.API]))
          )
        }
      } else {
        // Clear selections when switching to single-select
        this.table.selected = []
      }
    },
    getTargetUnits(target) {
      target = target.toLowerCase()
      let T = this.targets.options.find((t) => t.value == target)
      if (!!T) return T.units
      return null
    },
    onPercentiles(config) {
      const target = this.targets.selected
      const { invert, range, select } = config

      const indexes = this.probit["rank"].map((r, i) => (r >= range[0] / 100 && r <= range[1] / 100 ? i : undefined)).filter((x) => x)

      const ranks = this.probit["rank"].filter((el, i) => indexes.some((j) => i === j))
      const labels = this.probit[this.API].filter((el, i) => indexes.some((j) => i === j))
      const volumes = this.probit[target].filter((el, i) => indexes.some((j) => i === j))

      if (select) {
        this.onCreateGroup(labels)
      } else {
        this.charts.probit.data = []
        this.charts.probit.data.push({
          name: "Probit",
          type: "scatter",
          mode: "markers",
          // showlegend: false,
          x: volumes,
          y: ranks,
          text: labels,
          marker: { size: 6, color: this.targets.options.find((t) => t.value === target).color },
          selected: { marker: { size: 12, color: "#F44336" } },
        })
      }
    },
    onSummaryStats(dimensions) {
      let payload = this.requestPayload()
      delete payload.dimension
      payload["dimensions"] = dimensions
      Forecasting.get_multistats(this.item.uuid, payload).then((response) => {
        let csv = response.data
        var downloadLink = document.createElement("a")
        var blob = new Blob(["\ufeff", csv])
        var url = URL.createObjectURL(blob)
        downloadLink.href = url
        downloadLink.download = this.groups.selected ? `${this.selectedGroup.name}_${this.item.name}.csv` : `${this.item.name}.csv` //Name the file here
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
      })
    },
    filterAllowed(options, value) {
      return options.filter((o) => !(o.value == value && !this.isSuper))
    },
    storePaneSize(e) {
      e.forEach(p => p.size = Math.round(p.size))
      this.$store.commit('setPaneSize', this.clone(e))
    },
    resetPaneSize(e) {
      let obj = [
        { "min": 0, "max": 100, "size": 50 },
        { "min": 0, "max": 100, "size": 50 } 
      ]
      this.$store.commit('setPaneSize', this.clone(obj))
    },
    onSyncGroups() {
      let count = this.groups.list.filter(g => g.locked === true).length
      if (count > 0) {
        Predictions.sync_groups(this.item.uuid).then(response => {
          this.groups.list.filter(g => g.locked === true).map(g => g.saved = true)
          this.groups.list.filter(g => g.locked === false).map(g => g.saved = false)
          this.notify(response.data)
        })
      } else {
        this.notify("Please lock a group for syncing.")
      }
    },
    onWellSelect(well) {
      this.selectedWell = well
    }
  },
}
</script>